import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Section } from "@/components/Section";
import { supabase } from "@/integrations/supabase/client";
import {
  isAdmin,
  listAllPosts,
  deletePost,
  listKeywords,
  saveKeyword,
  deleteKeyword,
  addKeywordsBulk,
  listInternalLinks,
  saveInternalLink,
  deleteInternalLink,
  importSitemapLinks,
  getScheduleSettings,
  saveScheduleSettings,
  generatePostNow,
  listAllVideos,
  saveVideo,
  deleteVideo,
  listAllLocations,
  deleteLocation,
  generateLocationPage,
  listAllUsers,
  setUserRole,
  deleteUser,
  listPageSeo,
  savePageSeo,
  listServices,
  saveService,
  deleteService,
  generatePostsBatch,
  generateLocationsBatch,
  continueLocationsBatch,
  getLocalSeoBatchProgress,
  regenerateLocationPage,
  regenerateThinLocations,
  uploadPostImage,
  scrapeProductFromUrl,
  getCrmSettings,
  saveCrmSettings,
  listCrmVideos,
  deleteCrmVideo,
  syncCrmVideos,
  saveSystemPrompt,
  saveLocalSeoPrompt,
  syncYoutubeChannel,
  listPillarPages,
  savePillarPage,
  deletePillarPage,
  listProductModels,
  getProductModelForEdit,
  saveProductModel,
  deleteProductModel,
  generateProductModelFromBrief,
} from "@/lib/admin.functions";
import { formatDateBR } from "@/lib/format";
import { ApiKeysTab, CronExternoTab } from "@/components/admin/AdminSecretsAndCronTabs";

export const Route = createFileRoute("/admin/")({
  ssr: false,
  beforeLoad: async () => {
    const { redirect } = await import("@tanstack/react-router");
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: "/auth", search: {} });
  },
  head: () => ({
    meta: [
      { title: "Painel Administrativo | Empresa Brasil Online" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!mounted) return;
      if (!data.user) {
        navigate({ to: "/auth", replace: true, search: {} });
        return;
      }
      setEmail(data.user.email ?? null);
      try {
        const res = await isAdmin();
        if (!mounted) return;
        setAllowed(res.admin);
      } catch {
        setAllowed(false);
      } finally {
        if (mounted) setChecking(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  const posts = useQuery({
    queryKey: ["admin-posts"],
    queryFn: () => listAllPosts(),
    enabled: allowed,
  });

  const del = useMutation({
    mutationFn: (id: string) => deletePost({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-posts"] }),
  });

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true, search: {} });
  }

  if (checking) {
    return (
      <Section>
        <p className="text-sm text-muted-foreground">Validando sessão...</p>
      </Section>
    );
  }

  if (!allowed) {
    return (
      <Section>
        <div className="card-glass max-w-xl">
          <h1 className="text-xl font-bold">Sem permissão</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sua conta ({email}) ainda não tem permissão de administrador. Solicite o acesso ao responsável pelo site.
          </p>
          <button onClick={signOut} className="mt-4 text-xs underline">Sair</button>
        </div>
      </Section>
    );
  }

  return (
    <Section>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Blog · Painel</h1>
          <p className="text-sm text-muted-foreground">Logado como {email}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/post/$id" params={{ id: "new" }} className="btn-primary">Novo post</Link>
          <button onClick={signOut} className="rounded-lg border border-border px-4 py-2 text-sm">Sair</button>
        </div>
      </div>

      <AdminTabs posts={posts} del={del} />
    </Section>
  );
}

type TabKey = "posts" | "keywords" | "schedule" | "links" | "generate" | "videos" | "locations" | "users" | "sitemap" | "services" | "support" | "pillar" | "models" | "cron" | "apikeys";

function AdminTabs({ posts, del }: { posts: any; del: any }) {
  const [tab, setTab] = useState<TabKey>("posts");
  const tabs: { id: TabKey; label: string }[] = [
    { id: "posts", label: "Posts" },
    { id: "pillar", label: "Páginas Pilar" },
    { id: "models", label: "Modelos (Top 6)" },
    { id: "generate", label: "Gerar artigo (IA)" },
    { id: "keywords", label: "Palavras-chave" },
    { id: "schedule", label: "Agendamento" },
    { id: "links", label: "Link Building" },
    { id: "videos", label: "Vídeos" },
    { id: "services", label: "Serviços" },
    { id: "locations", label: "SEO Local" },
    { id: "users", label: "Usuários" },
    { id: "sitemap", label: "Sitemap & páginas" },
    { id: "support", label: "Suporte CRM (IA)" },
    { id: "cron", label: "Cron externo" },
    { id: "apikeys", label: "API Keys" },
  ];
  return (
    <div className="mt-8">
      <div className="flex flex-wrap gap-2 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "posts" && <PostsTab posts={posts} del={del} />}
        {tab === "pillar" && <PillarPagesTab />}
        {tab === "models" && <ModelsTab />}
        {tab === "generate" && <GenerateTab />}
        {tab === "keywords" && <KeywordsTab />}
        {tab === "schedule" && <ScheduleTab />}
        {tab === "links" && <LinksTab />}
        {tab === "videos" && <VideosTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "locations" && <LocationsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "sitemap" && <SitemapTab />}
        {tab === "support" && <SupportCrmTab />}
        {tab === "cron" && <CronExternoTab />}
        {tab === "apikeys" && <ApiKeysTab />}
      </div>
    </div>
  );
}

function PillarPagesTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-pillar-pages"], queryFn: () => listPillarPages() });
  const create = useMutation({
    mutationFn: (vars: { slug: string; title: string }) =>
      savePillarPage({ data: { slug: vars.slug, title: vars.title, items: [], published: true } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-pillar-pages"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => deletePillarPage({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-pillar-pages"] }),
  });
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");

  return (
    <div className="space-y-6">
      <div className="card-glass">
        <h3 className="text-base font-semibold">Páginas pilar de produtos</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Use estas páginas como hubs do tipo "Top 6" para enviar tráfego aos posts do blog. Cada página tem campos editáveis (título, intro, ranking de produtos com imagem, prós/contras, link de afiliado) e pode ser gerada automaticamente a partir de um resumo.
        </p>
        <form
          onSubmit={(e) => { e.preventDefault(); if (slug.trim() && title.trim()) { create.mutate({ slug: slug.trim(), title: title.trim() }); setSlug(""); setTitle(""); } }}
          className="mt-4 grid gap-3 md:grid-cols-[1fr_2fr_auto]"
        >
          <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
            placeholder="slug-da-pagina"
            className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Título (ex: Guia completo para abrir um MEI)"
            className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          <button type="submit" disabled={create.isPending} className="btn-primary whitespace-nowrap">
            {create.isPending ? "Criando..." : "Nova página pilar"}
          </button>
        </form>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Título</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Itens</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Atualizada em</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Carregando...</td></tr>}
            {list.data?.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Nenhuma página pilar ainda.</td></tr>}
            {list.data?.map((p: any) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{p.title}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">/{p.slug}</td>
                <td className="px-4 py-3">{p.items_count}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${p.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {p.published ? "Publicada" : "Rascunho"}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{formatDateBR(p.updated_at)}</td>
                <td className="px-4 py-3 text-right">
                  <Link to="/admin/pillar/$slug" params={{ slug: p.slug }} className="mr-3 text-primary hover:underline">Editar</Link>
                  <a href={`/${p.slug}`} target="_blank" rel="noopener" className="mr-3 text-primary hover:underline">Abrir</a>
                  <button onClick={() => { if (confirm(`Excluir "${p.title}"?`)) del.mutate(p.id); }} className="text-destructive hover:underline">Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ───────── Sitemap & páginas ─────────

const STATIC_PAGES: Array<{ path: string; label: string; group: string }> = [
  { path: "/", label: "Home", group: "Institucional" },
  { path: "/sobre", label: "Sobre", group: "Institucional" },
  { path: "/equipe", label: "Equipe", group: "Institucional" },
  { path: "/contato", label: "Contato", group: "Institucional" },
  { path: "/blog", label: "Blog", group: "Conteúdo" },
];

function SitemapTab() {
  const qc = useQueryClient();
  const seo = useQuery({ queryKey: ["admin-page-seo"], queryFn: () => listPageSeo() });
  const posts = useQuery({ queryKey: ["admin-posts"], queryFn: () => listAllPosts() });
  const locs = useQuery({ queryKey: ["admin-locations"], queryFn: () => listAllLocations() });
  const models = useQuery({ queryKey: ["admin-product-models"], queryFn: () => listProductModels() });

  const origin = typeof window !== "undefined" ? window.location.origin : "https://www.empresasbrasilonline.com.br";
  const seoMap = new Map<string, { title: string | null; description: string | null }>();
  (seo.data ?? []).forEach((r: any) => seoMap.set(r.path, { title: r.title, description: r.description }));

  const save = useMutation({
    mutationFn: (v: { path: string; title: string; description: string }) =>
      savePageSeo({ data: { path: v.path, title: v.title || null, description: v.description || null } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-page-seo"] }),
  });

  return (
    <div className="space-y-8">
      <div className="card-glass flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold">Sitemap XML do site</p>
          <p className="mt-1 text-xs text-muted-foreground">Envie este link no Google Search Console para indexar todas as páginas.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href="/sitemap-index.xml" target="_blank" rel="noopener" className="btn-primary">Abrir sitemap-index.xml</a>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(`${origin}/sitemap-index.xml`)}
            className="rounded-lg border border-border px-4 py-2 text-sm"
          >Copiar URL</button>
          <a href="/robots.txt" target="_blank" rel="noopener" className="rounded-lg border border-border px-4 py-2 text-sm">robots.txt</a>
        </div>
      </div>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Páginas estáticas</h2>
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-3">Página</th>
                <th className="px-3 py-3">URL</th>
                <th className="px-3 py-3">Title</th>
                <th className="px-3 py-3">Meta description</th>
                <th className="px-3 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {STATIC_PAGES.map((p) => (
                <SitemapRow key={p.path} page={p} initial={seoMap.get(p.path)} origin={origin} save={save} />
              ))}
              {(models.data ?? [])
                .filter((m: any) => m.active !== false)
                .map((m: any) => {
                  const path = `/modelos/${m.slug}`;
                  return (
                    <SitemapRow
                      key={path}
                      page={{ path, label: m.name, group: "Modelos" }}
                      initial={seoMap.get(path)}
                      origin={origin}
                      save={save}
                    />
                  );
                })}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Artigos do blog <span className="text-sm font-normal text-muted-foreground">({posts.data?.length ?? 0})</span></h2>
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-3">Título</th>
                <th className="px-3 py-3">URL</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {posts.isLoading && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Carregando...</td></tr>}
              {posts.data?.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Nenhum artigo.</td></tr>}
              {posts.data?.map((p: any) => {
                const path = `/blog/${p.slug}`;
                return (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-3 py-3 font-medium">{p.title}</td>
                    <td className="px-3 py-3 font-mono text-xs text-muted-foreground">{path}</td>
                    <td className="px-3 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${p.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                        {p.published ? "Publicado" : "Rascunho"}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => navigator.clipboard.writeText(`${origin}${path}`)} className="mr-3 text-primary hover:underline">Copiar link</button>
                      <Link to="/admin/post/$id" params={{ id: p.id }} className="text-primary hover:underline">Editar</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Páginas SEO local <span className="text-sm font-normal text-muted-foreground">({locs.data?.length ?? 0})</span></h2>
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-3">Título</th>
                <th className="px-3 py-3">URL</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {locs.isLoading && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Carregando...</td></tr>}
              {locs.data?.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Nenhuma página local.</td></tr>}
              {locs.data?.map((l: any) => {
                const path = `/servicos/${l.slug}`;
                return (
                  <tr key={l.id} className="border-t border-border">
                    <td className="px-3 py-3 font-medium">{l.title}</td>
                    <td className="px-3 py-3 font-mono text-xs text-muted-foreground">{path}</td>
                    <td className="px-3 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${l.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                        {l.published ? "Publicado" : "Rascunho"}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => navigator.clipboard.writeText(`${origin}${path}`)} className="mr-3 text-primary hover:underline">Copiar link</button>
                      <a href={path} target="_blank" rel="noopener" className="text-primary hover:underline">Abrir</a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function SitemapRow({
  page, initial, origin, save,
}: {
  page: { path: string; label: string; group: string };
  initial: { title: string | null; description: string | null } | undefined;
  origin: string;
  save: any;
}) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [copied, setCopied] = useState(false);
  const dirty = (title || "") !== (initial?.title ?? "") || (description || "") !== (initial?.description ?? "");
  const url = `${origin}${page.path}`;

  function copy() {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }

  return (
    <tr className="border-t border-border align-top">
      <td className="px-3 py-3">
        <div className="font-medium">{page.label}</div>
        <div className="text-xs text-muted-foreground">{page.group}</div>
      </td>
      <td className="px-3 py-3 font-mono text-xs text-muted-foreground">{page.path}</td>
      <td className="px-3 py-3">
        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Title da página (deixe vazio = usar o padrão)"
          className="w-full rounded-md border border-border bg-background/60 px-2 py-1.5 text-xs"
        />
      </td>
      <td className="px-3 py-3">
        <textarea
          rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Meta description (até 160 caracteres)"
          className="w-full rounded-md border border-border bg-background/60 px-2 py-1.5 text-xs"
        />
      </td>
      <td className="px-3 py-3 text-right">
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex gap-2">
            <a href={page.path} target="_blank" rel="noopener" className="text-xs text-primary hover:underline">Abrir</a>
            <button onClick={copy} className="text-xs text-primary hover:underline">{copied ? "Copiado!" : "Copiar"}</button>
          </div>
          <button
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate({ path: page.path, title, description })}
            className="rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs text-primary disabled:opacity-40"
          >
            {save.isPending ? "..." : "Salvar"}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ───────── Users ─────────

function UsersTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-users"], queryFn: () => listAllUsers() });
  const setRole = useMutation({
    mutationFn: (vars: { user_id: string; role: "admin" | "editor" | "user"; grant: boolean }) =>
      setUserRole({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
  const del = useMutation({
    mutationFn: (user_id: string) => deleteUser({ data: { user_id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  const pending = (list.data ?? []).filter((u: any) => !u.approved);
  const approved = (list.data ?? []).filter((u: any) => u.approved);

  return (
    <div className="space-y-8">
      <p className="text-sm text-muted-foreground">
        Aprove novas contas concedendo um papel (Usuário, Editor ou Administrador). Usuários sem papel ficam aguardando aprovação.
      </p>

      <section>
        <h2 className="mb-3 text-lg font-semibold">
          Pedidos de aprovação <span className="text-sm font-normal text-muted-foreground">({pending.length})</span>
        </h2>
        <UsersTable
          rows={pending}
          loading={list.isLoading}
          empty="Nenhum pedido pendente."
          setRole={setRole}
          del={del}
        />
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">
          Contas aprovadas <span className="text-sm font-normal text-muted-foreground">({approved.length})</span>
        </h2>
        <UsersTable
          rows={approved}
          loading={list.isLoading}
          empty="Nenhuma conta aprovada ainda."
          setRole={setRole}
          del={del}
        />
      </section>

      {(setRole.error || del.error) && (
        <p className="text-sm text-destructive">
          {((setRole.error || del.error) as Error).message}
        </p>
      )}
    </div>
  );
}

function UsersTable({
  rows, loading, empty, setRole, del,
}: { rows: any[]; loading: boolean; empty: string; setRole: any; del: any }) {
  const ROLES: Array<{ key: "admin" | "editor" | "user"; label: string }> = [
    { key: "admin", label: "Admin" },
    { key: "editor", label: "Editor" },
    { key: "user", label: "Usuário" },
  ];
  return (
    <div className="overflow-hidden rounded-2xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-4 py-3">E-mail</th>
            <th className="px-4 py-3">Criado</th>
            <th className="px-4 py-3">Último acesso</th>
            <th className="px-4 py-3">Papéis</th>
            <th className="px-4 py-3 text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Carregando...</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">{empty}</td></tr>}
          {rows.map((u) => (
            <tr key={u.id} className="border-t border-border align-top">
              <td className="px-4 py-3">
                <div className="font-medium">{u.email || "(sem e-mail)"}</div>
                <div className="text-xs text-muted-foreground">
                  {u.email_confirmed_at ? "E-mail confirmado" : "E-mail não confirmado"}
                </div>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{u.created_at ? formatDateBR(u.created_at) : "—"}</td>
              <td className="px-4 py-3 text-muted-foreground">{u.last_sign_in_at ? formatDateBR(u.last_sign_in_at) : "Nunca"}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                  {u.roles.length === 0 && <span className="text-xs text-muted-foreground">Sem papel</span>}
                  {u.roles.map((r: string) => (
                    <span key={r} className="rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">{r}</span>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap justify-end gap-2">
                  {ROLES.map((r) => {
                    const has = u.roles.includes(r.key);
                    return (
                      <button
                        key={r.key}
                        disabled={setRole.isPending}
                        onClick={() => setRole.mutate({ user_id: u.id, role: r.key, grant: !has })}
                        className={`rounded-lg border px-2.5 py-1 text-xs transition ${
                          has
                            ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
                            : "border-border text-muted-foreground hover:text-foreground"
                        }`}
                        title={has ? `Remover ${r.label}` : `Conceder ${r.label}`}
                      >
                        {has ? `− ${r.label}` : `+ ${r.label}`}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => { if (confirm(`Excluir a conta ${u.email}? Esta ação é permanente.`)) del.mutate(u.id); }}
                    className="rounded-lg border border-destructive/40 px-2.5 py-1 text-xs text-destructive hover:bg-destructive/10"
                  >
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PostsTab({ posts, del }: { posts: any; del: any }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Título</th>
              <th className="px-4 py-3">Categoria</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Atualizado</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {posts.isLoading && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Carregando...</td></tr>
            )}
            {posts.data?.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Nenhum post criado ainda.</td></tr>
            )}
            {posts.data?.map((p: any) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{p.title}</td>
                <td className="px-4 py-3">{p.category}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${p.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {p.published ? "Publicado" : "Rascunho"}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{p.updated_at ? formatDateBR(p.updated_at) : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-3">
                    <Link to="/admin/post/$id" params={{ id: p.id }} className="text-primary hover:underline">Editar</Link>
                    <button
                      onClick={() => {
                        if (confirm(`Excluir o post "${p.title}"?`)) del.mutate(p.id);
                      }}
                      className="text-destructive hover:underline"
                    >
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
    </div>
  );
}

// ───────── Keywords ─────────

const ARTICLE_TYPE_OPTIONS: Array<{ value: "info" | "review" | "comparativo" | "melhores" | "empresa"; label: string }> = [
  { value: "info", label: "Informacional" },
  { value: "review", label: "Review" },
  { value: "comparativo", label: "Comparativo" },
  { value: "melhores", label: "Melhores (lista)" },
  { value: "empresa", label: "Empresa (perfil / NAP)" },
];

type BusinessData = {
  nap: { name: string; address: string; phone: string; hours: string };
  description: string;
  socials: { instagram: string; facebook: string; linkedin: string; youtube: string; website: string };
  google_maps_embed: string;
};
const EMPTY_BUSINESS: BusinessData = {
  nap: { name: "", address: "", phone: "", hours: "" },
  description: "",
  socials: { instagram: "", facebook: "", linkedin: "", youtube: "", website: "" },
  google_maps_embed: "",
};
function normalizeBusiness(raw: any): BusinessData {
  if (!raw || typeof raw !== "object") return { ...EMPTY_BUSINESS, nap: { ...EMPTY_BUSINESS.nap }, socials: { ...EMPTY_BUSINESS.socials } };
  const nap = raw.nap ?? {};
  const socials = raw.socials ?? {};
  return {
    nap: {
      name: String(nap.name ?? ""),
      address: String(nap.address ?? ""),
      phone: String(nap.phone ?? ""),
      hours: String(nap.hours ?? ""),
    },
    description: String(raw.description ?? ""),
    socials: {
      instagram: String(socials.instagram ?? ""),
      facebook: String(socials.facebook ?? ""),
      linkedin: String(socials.linkedin ?? ""),
      youtube: String(socials.youtube ?? ""),
      website: String(socials.website ?? ""),
    },
    google_maps_embed: String(raw.google_maps_embed ?? ""),
  };
}

function KeywordsTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-keywords"], queryFn: () => listKeywords() });
  const [keyword, setKeyword] = useState("");
  const [topic, setTopic] = useState("");
  const [articleType, setArticleType] = useState<"info" | "review" | "comparativo" | "melhores" | "empresa">("info");
  const [productName, setProductName] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [productImage, setProductImage] = useState("");
  const [productImageAlt, setProductImageAlt] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productRating, setProductRating] = useState("");
  const [productReviews, setProductReviews] = useState("");
  const [productFeatures, setProductFeatures] = useState("");
  const [productCta, setProductCta] = useState("");
  const [productMarketplace, setProductMarketplace] = useState("");
  const [productCtaBg, setProductCtaBg] = useState("#FF6A00");
  const [productCtaText, setProductCtaText] = useState("#FFFFFF");
  const [uploadingKwImg, setUploadingKwImg] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [scrapeErr, setScrapeErr] = useState<string | null>(null);
  const [scrapeOk, setScrapeOk] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [extraProducts, setExtraProducts] = useState<any[]>([]);
  const [typeFilter, setTypeFilter] = useState<"all" | "info" | "review" | "comparativo" | "melhores" | "empresa">("all");
  const [searchFilter, setSearchFilter] = useState("");
  const [mergeIds, setMergeIds] = useState<string[]>([]);
  const [business, setBusiness] = useState<BusinessData>(() => ({
    ...EMPTY_BUSINESS,
    nap: { ...EMPTY_BUSINESS.nap },
    socials: { ...EMPTY_BUSINESS.socials },
  }));
  const [scheduledFor, setScheduledFor] = useState<string>("");
  const [repeatIntervalHours, setRepeatIntervalHours] = useState<string>("");
  const [keywordVariations, setKeywordVariations] = useState<string>("");
  const [scheduledDates, setScheduledDates] = useState<string[]>([]);

  function addExtraProduct() {
    setExtraProducts((arr) => [
      ...arr,
      {
        product_name: "", product_url: "", product_image: "", product_image_alt: "",
        product_price: "", product_rating: null, product_reviews_count: null,
        product_features: [], product_cta_label: "", product_marketplace: "",
        product_cta_bg_color: "#FF6A00", product_cta_text_color: "#FFFFFF",
      },
    ]);
  }
  function updateExtraProduct(i: number, patch: any) {
    setExtraProducts((arr) => arr.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }
  function removeExtraProduct(i: number) {
    setExtraProducts((arr) => arr.filter((_, idx) => idx !== i));
  }
  async function uploadExtraImage(i: number, file: File) {
    const buf = await file.arrayBuffer();
    let bin = ""; const bytes = new Uint8Array(buf);
    for (let j = 0; j < bytes.length; j++) bin += String.fromCharCode(bytes[j]);
    const base64 = btoa(bin);
    const r = await uploadPostImage({ data: { fileName: file.name, contentType: file.type || "image/jpeg", base64 } });
    updateExtraProduct(i, { product_image: r.url });
  }
  const formRef = useRef<HTMLFormElement>(null);

  async function handleScrapeProduct() {
    const url = scrapeUrl.trim();
    if (!url) return;
    setScrapeErr(null); setScrapeOk(null); setScraping(true);
    try {
      const r = await scrapeProductFromUrl({ data: { url } });
      if (r.title) setProductName(r.title);
      if (r.image) setProductImage(r.image);
      if (r.title && !productImageAlt) setProductImageAlt(r.title);
      if (r.price) setProductPrice(r.price);
      if (r.rating != null) setProductRating(String(r.rating));
      if (r.reviews_count != null) setProductReviews(String(r.reviews_count));
      if (r.features?.length) setProductFeatures(r.features.join("\n"));
      if (r.marketplace && !productMarketplace) setProductMarketplace(r.marketplace);
      if (!productUrl) setProductUrl(url);
      setScrapeOk("Dados importados! Revise antes de salvar.");
    } catch (e) {
      setScrapeErr(e instanceof Error ? e.message : "Falha ao importar do link");
    } finally {
      setScraping(false);
    }
  }

  function loadKeywordIntoForm(k: any) {
    setEditingId(k.id);
    setKeyword(k.keyword ?? "");
    setTopic(k.topic ?? "");
    setArticleType((k.article_type ?? "info") as any);
    setProductName(k.product_name ?? "");
    setProductUrl(k.product_url ?? "");
    setProductImage(k.product_image ?? "");
    setProductImageAlt(k.product_image_alt ?? "");
    setProductPrice(k.product_price ?? "");
    setProductRating(k.product_rating != null ? String(k.product_rating) : "");
    setProductReviews(k.product_reviews_count != null ? String(k.product_reviews_count) : "");
    setProductFeatures(Array.isArray(k.product_features) ? k.product_features.join("\n") : "");
    setProductCta(k.product_cta_label ?? "");
    setProductMarketplace(k.product_marketplace ?? "");
    setProductCtaBg(k.product_cta_bg_color ?? "#FF6A00");
    setProductCtaText(k.product_cta_text_color ?? "#FFFFFF");
    setExtraProducts(Array.isArray(k.extra_products) ? k.extra_products : []);
    setBusiness(normalizeBusiness((k as any).business_data));
    setScheduledFor(
      (k as any).scheduled_for
        ? new Date((k as any).scheduled_for).toISOString().slice(0, 16)
        : "",
    );
    setRepeatIntervalHours(
      (k as any).repeat_interval_hours != null ? String((k as any).repeat_interval_hours) : "",
    );
    setKeywordVariations(
      Array.isArray((k as any).keyword_variations) ? (k as any).keyword_variations.join("\n") : "",
    );
    setScheduledDates(
      Array.isArray((k as any).scheduled_dates)
        ? (k as any).scheduled_dates
            .map((d: string) => {
              try { return new Date(d).toISOString().slice(0, 16); } catch { return ""; }
            })
            .filter((s: string) => !!s)
        : [],
    );
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  function duplicateKeywordIntoForm(k: any) {
    loadKeywordIntoForm(k);
    setEditingId(null);
    setKeyword(`${k.keyword ?? ""} (cópia)`.trim());
  }

  function mergeSelectedIntoForm() {
    const all = (list.data ?? []) as any[];
    const picked = mergeIds.map((id) => all.find((x) => x.id === id)).filter(Boolean);
    if (picked.length < 2) {
      alert("Selecione pelo menos 2 palavras-chave para mesclar.");
      return;
    }
    // Primary = first picked (with its main product). Extras = main products of the rest
    // plus any extras already attached to any picked keyword.
    const primary = picked[0];
    loadKeywordIntoForm(primary);
    setEditingId(null);
    const combinedName = picked.map((p: any) => p.keyword).join(" vs ");
    setKeyword(combinedName);
    setArticleType("review");
    const extras: any[] = [];
    for (let i = 1; i < picked.length; i++) {
      const p: any = picked[i];
      if (p.product_name && p.product_url) {
        extras.push({
          product_name: p.product_name, product_url: p.product_url,
          product_image: p.product_image, product_image_alt: p.product_image_alt,
          product_price: p.product_price, product_rating: p.product_rating,
          product_reviews_count: p.product_reviews_count,
          product_features: p.product_features ?? [],
          product_cta_label: p.product_cta_label, product_marketplace: p.product_marketplace,
          product_cta_bg_color: p.product_cta_bg_color ?? "#FF6A00",
          product_cta_text_color: p.product_cta_text_color ?? "#FFFFFF",
        });
      }
      if (Array.isArray(p.extra_products)) extras.push(...p.extra_products);
    }
    // Also keep extras already in primary
    if (Array.isArray(primary.extra_products)) extras.unshift(...primary.extra_products);
    // Dedup by product_url
    const seen = new Set<string>();
    const deduped = extras.filter((e: any) => {
      const k = (e.product_url ?? "").trim();
      if (!k || seen.has(k)) return false;
      seen.add(k); return true;
    });
    setExtraProducts(deduped);
    setMergeIds([]);
  }

  async function handleKwImageUpload(file: File) {
    if (!file) return;
    setUploadErr(null);
    setUploadingKwImg(true);
    try {
      const buf = await file.arrayBuffer();
      let bin = "";
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const base64 = btoa(bin);
      const r = await uploadPostImage({ data: {
        fileName: file.name, contentType: file.type || "image/jpeg", base64,
      } });
      setProductImage(r.url);
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : "Falha no upload");
    } finally {
      setUploadingKwImg(false);
    }
  }

  function resetProductForm() {
    setEditingId(null);
    setKeyword(""); setTopic(""); setArticleType("info");
    setProductName(""); setProductUrl(""); setProductImage(""); setProductImageAlt("");
    setProductPrice(""); setProductRating(""); setProductReviews("");
    setProductFeatures(""); setProductCta(""); setProductMarketplace("");
    setProductCtaBg("#FF6A00"); setProductCtaText("#FFFFFF");
    setExtraProducts([]);
    setBusiness({
      ...EMPTY_BUSINESS,
      nap: { ...EMPTY_BUSINESS.nap },
      socials: { ...EMPTY_BUSINESS.socials },
    });
    setScheduledFor("");
    setRepeatIntervalHours("");
    setKeywordVariations("");
    setScheduledDates([]);
  }

  const save = useMutation({
    mutationFn: () =>
      saveKeyword({
        data: {
          id: editingId ?? undefined,
          keyword: keyword.trim(),
          topic: topic.trim(),
          active: true,
          article_type: articleType,
          product_name: productName.trim() || null,
          product_url: productUrl.trim() || null,
          product_image: productImage.trim() || null,
          product_image_alt: productImageAlt.trim() || null,
          product_price: productPrice.trim() || null,
          product_rating: productRating.trim() ? Number(productRating.replace(",", ".")) : null,
          product_reviews_count: productReviews.trim() ? Math.trunc(Number(productReviews.replace(/\D+/g, ""))) : null,
          product_features: productFeatures.split("\n").map((s) => s.trim()).filter(Boolean),
          product_cta_label: productCta.trim() || null,
          product_marketplace: productMarketplace.trim() || null,
          product_cta_bg_color: productCtaBg.trim() || null,
          product_cta_text_color: productCtaText.trim() || null,
          extra_products: extraProducts
            .map((p) => ({
              product_name: (p.product_name ?? "").trim() || null,
              product_url: (p.product_url ?? "").trim() || null,
              product_image: (p.product_image ?? "").trim() || null,
              product_image_alt: (p.product_image_alt ?? "").trim() || null,
              product_price: (p.product_price ?? "").trim() || null,
              product_rating: p.product_rating != null && p.product_rating !== "" ? Number(String(p.product_rating).replace(",", ".")) : null,
              product_reviews_count: p.product_reviews_count != null && p.product_reviews_count !== "" ? Math.trunc(Number(String(p.product_reviews_count).replace(/\D+/g, ""))) : null,
              product_features: Array.isArray(p.product_features) ? p.product_features : String(p.product_features ?? "").split("\n").map((s: string) => s.trim()).filter(Boolean),
              product_cta_label: (p.product_cta_label ?? "").trim() || null,
              product_marketplace: (p.product_marketplace ?? "").trim() || null,
              product_cta_bg_color: (p.product_cta_bg_color ?? "").trim() || null,
              product_cta_text_color: (p.product_cta_text_color ?? "").trim() || null,
            }))
            .filter((p) => p.product_name && p.product_url),
          business_data: articleType === "empresa" ? {
            nap: {
              name: business.nap.name.trim(),
              address: business.nap.address.trim(),
              phone: business.nap.phone.trim(),
              hours: business.nap.hours.trim(),
            },
            description: business.description.trim(),
            socials: {
              instagram: business.socials.instagram.trim(),
              facebook: business.socials.facebook.trim(),
              linkedin: business.socials.linkedin.trim(),
              youtube: business.socials.youtube.trim(),
              website: business.socials.website.trim(),
            },
            google_maps_embed: business.google_maps_embed.trim(),
          } : null,
          scheduled_for: articleType === "empresa" && scheduledFor ? scheduledFor : null,
          repeat_interval_hours:
            articleType === "empresa" && repeatIntervalHours.trim()
              ? Math.max(1, Math.min(8760, Math.trunc(Number(repeatIntervalHours.replace(/\D+/g, "")) || 0)))
              : null,
          keyword_variations: articleType === "empresa"
            ? keywordVariations.split(/\r?\n/).map((s) => s.trim()).filter((s) => s.length >= 2)
            : [],
          scheduled_dates: articleType === "empresa"
            ? scheduledDates.map((s) => s.trim()).filter(Boolean)
            : [],
        },
      }),
    onSuccess: () => { resetProductForm(); qc.invalidateQueries({ queryKey: ["admin-keywords"] }); },
  });
  const toggle = useMutation({
    mutationFn: (k: any) => saveKeyword({ data: {
      id: k.id, keyword: k.keyword, topic: k.topic ?? "", active: !k.active,
      article_type: k.article_type ?? "info",
      product_name: k.product_name ?? null,
      product_image: k.product_image ?? null,
      product_image_alt: k.product_image_alt ?? null,
      product_url: k.product_url ?? null,
      product_price: k.product_price ?? null,
      product_rating: k.product_rating ?? null,
      product_reviews_count: k.product_reviews_count ?? null,
      product_features: k.product_features ?? [],
      product_cta_label: k.product_cta_label ?? null,
      product_marketplace: k.product_marketplace ?? null,
      product_cta_bg_color: k.product_cta_bg_color ?? null,
      product_cta_text_color: k.product_cta_text_color ?? null,
      extra_products: Array.isArray(k.extra_products) ? k.extra_products : [],
      business_data: (k as any).business_data ?? null,
    } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-keywords"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteKeyword({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-keywords"] }),
  });
  const [genBusyId, setGenBusyId] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const generateOne = useMutation({
    mutationFn: (id: string) => generatePostNow({ data: { keywordIds: [id] } }),
    onMutate: (id: string) => { setGenBusyId(id); setGenError(null); },
    onSuccess: (r: any) => {
      setGenBusyId(null);
      qc.invalidateQueries({ queryKey: ["admin-keywords"] });
      qc.invalidateQueries({ queryKey: ["admin-posts"] });
      alert(`Artigo gerado: ${r?.title ?? r?.slug ?? "ok"}`);
    },
    onError: (e: unknown) => {
      setGenBusyId(null);
      setGenError(e instanceof Error ? e.message : "Falha ao gerar artigo");
    },
  });
  return (
    <div className="space-y-6">
      <form
        ref={formRef}
        onSubmit={(e) => { e.preventDefault(); if (keyword.trim()) save.mutate(); }}
        className="card-glass space-y-4"
      >
        {editingId && (
          <div className="flex items-center justify-between rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-xs">
            <span className="font-semibold text-primary">✏️ Editando palavra-chave</span>
            <button type="button" onClick={resetProductForm} className="text-muted-foreground hover:text-foreground">
              Cancelar edição
            </button>
          </div>
        )}
        <div className="grid gap-3 md:grid-cols-2">
          <input
            required value={keyword} onChange={(e) => setKeyword(e.target.value)}
            placeholder="Palavra-chave (ex: como abrir uma empresa MEI 2026)"
            className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
          />
          <select
            value={articleType}
            onChange={(e) => setArticleType(e.target.value as any)}
            className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
          >
            {ARTICLE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>Tipo: {o.label}</option>
            ))}
          </select>
        </div>
        <input
          value={topic} onChange={(e) => setTopic(e.target.value)}
          placeholder="Ângulo / assunto que o redator deve abordar (opcional)"
          className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
        />

        <details className="rounded-xl border border-primary/30 bg-primary/5 p-3" open={articleType !== "info"}>
          <summary className="cursor-pointer text-sm font-semibold text-foreground">
            🛒 Produto afiliado (opcional — será inserido no artigo como card)
          </summary>

          <div className="mt-3 rounded-lg border border-primary/40 bg-background/60 p-3 space-y-2">
            <label className="text-xs font-semibold text-foreground">⚡ Importar do link do produto (Amazon / Mercado Livre / outros)</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input value={scrapeUrl} onChange={(e) => setScrapeUrl(e.target.value)}
                placeholder="Cole aqui o link do produto"
                className="flex-1 rounded-lg border border-border bg-background/80 px-3 py-2 text-sm font-mono" />
              <button type="button" onClick={handleScrapeProduct} disabled={scraping || !scrapeUrl.trim()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                {scraping ? "Importando..." : "Importar dados"}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Preenche título, imagem, preço, nota, depoimentos, características e descrição automaticamente. Revise os campos antes de salvar.
            </p>
            {scrapeErr && <p className="text-xs text-destructive">{scrapeErr}</p>}
            {scrapeOk && <p className="text-xs text-emerald-600">{scrapeOk}</p>}
          </div>

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <input value={productName} onChange={(e) => setProductName(e.target.value)}
              placeholder="Nome do produto"
              className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            <input value={productMarketplace} onChange={(e) => setProductMarketplace(e.target.value)}
              placeholder="Marketplace (Amazon, Mercado Livre...)"
              className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            <input value={productUrl} onChange={(e) => setProductUrl(e.target.value)}
              placeholder="Link de afiliado (https://...)"
              className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />

            <div className="md:col-span-2 space-y-2 rounded-lg border border-border bg-background/40 p-3">
              <label className="text-xs font-semibold text-foreground">Imagem do produto</label>
              <input value={productImage} onChange={(e) => setProductImage(e.target.value)}
                placeholder="URL da imagem (ou faça upload abaixo)"
                className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
              <div className="flex flex-wrap items-center gap-2">
                <label className="cursor-pointer rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs text-primary hover:bg-primary/20">
                  {uploadingKwImg ? "Enviando..." : "📤 Upload direto da imagem"}
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleKwImageUpload(f);
                      e.currentTarget.value = "";
                    }} />
                </label>
                <span className="text-[11px] text-muted-foreground">PNG/JPG/WebP até ~10 MB.</span>
              </div>
              {uploadErr && <p className="text-xs text-destructive">{uploadErr}</p>}
              {productImage && (
                <div className="flex items-start gap-3">
                  <img src={productImage} alt={productImageAlt || productName || "Produto"}
                    className="h-24 w-24 rounded-lg object-cover border border-border" loading="lazy" />
                  <div className="flex-1">
                    <input value={productImageAlt} onChange={(e) => setProductImageAlt(e.target.value)}
                      placeholder="Alt text da imagem (acessibilidade + SEO)"
                      className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Descreva a imagem em uma frase — aparece para leitores de tela e melhora o ranking no Google Imagens.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <input value={productPrice} onChange={(e) => setProductPrice(e.target.value)}
              placeholder="Preço (ex: R$ 2.499,00)"
              className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            <input value={productCta} onChange={(e) => setProductCta(e.target.value)}
              placeholder="Texto do botão (CTA)"
              className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            <input type="number" min={0} max={5} step="0.1"
              value={productRating} onChange={(e) => setProductRating(e.target.value)}
              placeholder="Nota (0–5)"
              className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            <input type="number" min={0} step="1"
              value={productReviews} onChange={(e) => setProductReviews(e.target.value)}
              placeholder="Quantidade de depoimentos"
              className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            <textarea rows={3} value={productFeatures} onChange={(e) => setProductFeatures(e.target.value)}
              placeholder={"Características (uma por linha)"}
              className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />

            <div className="md:col-span-2 grid gap-3 rounded-lg border border-border bg-background/40 p-3 sm:grid-cols-[1fr_1fr_auto]">
              <label className="flex flex-col gap-1 text-xs font-semibold text-foreground">
                Cor de fundo do botão CTA
                <div className="flex items-center gap-2">
                  <input type="color" value={productCtaBg} onChange={(e) => setProductCtaBg(e.target.value)}
                    className="h-9 w-12 cursor-pointer rounded border border-border bg-background" />
                  <input value={productCtaBg} onChange={(e) => setProductCtaBg(e.target.value)}
                    placeholder="#FF6A00"
                    className="flex-1 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
                </div>
              </label>
              <label className="flex flex-col gap-1 text-xs font-semibold text-foreground">
                Cor do texto do botão CTA
                <div className="flex items-center gap-2">
                  <input type="color" value={productCtaText} onChange={(e) => setProductCtaText(e.target.value)}
                    className="h-9 w-12 cursor-pointer rounded border border-border bg-background" />
                  <input value={productCtaText} onChange={(e) => setProductCtaText(e.target.value)}
                    placeholder="#FFFFFF"
                    className="flex-1 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
                </div>
              </label>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-foreground">Pré-visualização</span>
                <span
                  className="inline-flex items-center justify-center rounded-full px-5 py-2 text-xs font-extrabold uppercase tracking-wider"
                  style={{
                    background: `linear-gradient(135deg, ${productCtaBg}, color-mix(in oklab, ${productCtaBg} 70%, #000))`,
                    color: productCtaText,
                  }}
                >
                  {productCta.trim() || "Comprar agora"}
                </span>
              </div>
            </div>
          </div>
        </details>

        <details className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3" open={extraProducts.length > 0}>
          <summary className="cursor-pointer text-sm font-semibold text-foreground">
            🆚 Produtos adicionais (unifica nesta palavra-chave — gera review comparativo automático)
          </summary>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Adicione 1 ou mais produtos para que o agendamento gere automaticamente um artigo comparativo entre o produto principal acima e estes.
          </p>
          <div className="mt-3 space-y-3">
            {extraProducts.map((p, i) => (
              <div key={i} className="rounded-lg border border-amber-500/40 bg-background/60 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-amber-700">Produto #{i + 2}</span>
                  <button type="button" onClick={() => removeExtraProduct(i)} className="text-xs text-destructive hover:underline">Remover</button>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  <input value={p.product_name ?? ""} onChange={(e) => updateExtraProduct(i, { product_name: e.target.value })}
                    placeholder="Nome do produto" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                  <input value={p.product_marketplace ?? ""} onChange={(e) => updateExtraProduct(i, { product_marketplace: e.target.value })}
                    placeholder="Marketplace" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                  <input value={p.product_url ?? ""} onChange={(e) => updateExtraProduct(i, { product_url: e.target.value })}
                    placeholder="Link de afiliado (https://...)"
                    className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
                  <div className="md:col-span-2 flex flex-wrap items-center gap-2">
                    <input value={p.product_image ?? ""} onChange={(e) => updateExtraProduct(i, { product_image: e.target.value })}
                      placeholder="URL da imagem"
                      className="flex-1 min-w-[240px] rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
                    <label className="cursor-pointer rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs text-primary hover:bg-primary/20">
                      📤 Upload
                      <input type="file" accept="image/*" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadExtraImage(i, f); e.currentTarget.value = ""; }} />
                    </label>
                    {p.product_image && (
                      <img src={p.product_image} alt={p.product_image_alt || p.product_name || "Produto"}
                        className="h-12 w-12 rounded object-cover border border-border" loading="lazy" />
                    )}
                  </div>
                  <input value={p.product_image_alt ?? ""} onChange={(e) => updateExtraProduct(i, { product_image_alt: e.target.value })}
                    placeholder="Alt text da imagem"
                    className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                  <input value={p.product_price ?? ""} onChange={(e) => updateExtraProduct(i, { product_price: e.target.value })}
                    placeholder="Preço (R$)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                  <input value={p.product_cta_label ?? ""} onChange={(e) => updateExtraProduct(i, { product_cta_label: e.target.value })}
                    placeholder="Texto do botão (CTA)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                  <input type="number" min={0} max={5} step="0.1" value={p.product_rating ?? ""}
                    onChange={(e) => updateExtraProduct(i, { product_rating: e.target.value })}
                    placeholder="Nota (0-5)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                  <input type="number" min={0} step="1" value={p.product_reviews_count ?? ""}
                    onChange={(e) => updateExtraProduct(i, { product_reviews_count: e.target.value })}
                    placeholder="Qtd. depoimentos" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                  <textarea rows={2}
                    value={Array.isArray(p.product_features) ? p.product_features.join("\n") : (p.product_features ?? "")}
                    onChange={(e) => updateExtraProduct(i, { product_features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    placeholder="Características (uma por linha)"
                    className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                </div>
              </div>
            ))}
            <button type="button" onClick={addExtraProduct}
              className="rounded-lg border border-dashed border-amber-500/60 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-500/20">
              + Adicionar outro produto a esta palavra-chave
            </button>
          </div>
        </details>

        <details className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3" open={articleType === "empresa"}>
          <summary className="cursor-pointer text-sm font-semibold text-foreground">
            🏢 Perfil da empresa parceira (NAP, redes sociais, mapa e agendamento — para artigos do tipo Empresa)
          </summary>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Preencha estes dados para gerar uma matéria de indicação editorial. O cartão institucional (com nome, endereço, telefone, horário, redes sociais e mapa) é sempre renderizado como um bloco separado no topo do artigo — independentemente do que a IA escrever.
          </p>

          <div className="mt-3 space-y-3 rounded-lg border border-emerald-500/40 bg-background/60 p-3">
            <div className="grid gap-2 md:grid-cols-2">
              <input value={business.nap.name} onChange={(e) => setBusiness((b) => ({ ...b, nap: { ...b.nap, name: e.target.value } }))}
                placeholder="Nome da empresa" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
              <input value={business.nap.phone} onChange={(e) => setBusiness((b) => ({ ...b, nap: { ...b.nap, phone: e.target.value } }))}
                placeholder="Telefone (com DDD)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
              <input value={business.nap.address} onChange={(e) => setBusiness((b) => ({ ...b, nap: { ...b.nap, address: e.target.value } }))}
                placeholder="Endereço completo (rua, número, bairro, cidade)" className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
              <input value={business.nap.hours} onChange={(e) => setBusiness((b) => ({ ...b, nap: { ...b.nap, hours: e.target.value } }))}
                placeholder="Horário de atendimento (ex: Seg a Sex, 9h–18h)" className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
            <textarea rows={4} value={business.description}
              onChange={(e) => setBusiness((b) => ({ ...b, description: e.target.value }))}
              placeholder="Descrição institucional / história da empresa (a IA usa como base para escrever o artigo — não copia literalmente)"
              className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />

            <div className="grid gap-2 md:grid-cols-2">
              <input value={business.socials.website} onChange={(e) => setBusiness((b) => ({ ...b, socials: { ...b.socials, website: e.target.value } }))}
                placeholder="Site oficial (https://...)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
              <input value={business.socials.instagram} onChange={(e) => setBusiness((b) => ({ ...b, socials: { ...b.socials, instagram: e.target.value } }))}
                placeholder="Instagram (URL completa)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
              <input value={business.socials.facebook} onChange={(e) => setBusiness((b) => ({ ...b, socials: { ...b.socials, facebook: e.target.value } }))}
                placeholder="Facebook (URL completa)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
              <input value={business.socials.linkedin} onChange={(e) => setBusiness((b) => ({ ...b, socials: { ...b.socials, linkedin: e.target.value } }))}
                placeholder="LinkedIn (URL completa)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
              <input value={business.socials.youtube} onChange={(e) => setBusiness((b) => ({ ...b, socials: { ...b.socials, youtube: e.target.value } }))}
                placeholder="YouTube (URL do canal)" className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
            </div>

            <label className="block">
              <span className="text-xs font-semibold text-foreground">Código de incorporação do Google Maps</span>
              <textarea rows={3} value={business.google_maps_embed}
                onChange={(e) => setBusiness((b) => ({ ...b, google_maps_embed: e.target.value }))}
                placeholder={'Cole aqui o <iframe src="https://www.google.com/maps/embed?..." ...></iframe> do Google Maps'}
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-xs font-mono" />
              <p className="mt-1 text-[11px] text-muted-foreground">
                No Google Maps: Compartilhar → Incorporar um mapa → Copiar HTML. Só o embed oficial do Google é aceito por segurança.
              </p>
            </label>
          </div>

          <div className="mt-4 rounded-lg border border-sky-500/40 bg-sky-500/5 p-3 space-y-2">
            <label className="block">
              <span className="text-sm font-semibold text-foreground">📅 Agendar publicação (opcional)</span>
              <input
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                className="mt-2 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Quando preenchido, o artigo desta empresa será gerado e publicado automaticamente pelo agendador a partir dessa data/hora (a próxima execução do cron pega esta palavra-chave com prioridade). Deixe em branco para entrar na rotação normal.
              </p>
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-foreground">🔁 Repetir a cada (horas)</span>
              <input
                type="number"
                min={1}
                max={8760}
                step={1}
                value={repeatIntervalHours}
                onChange={(e) => setRepeatIntervalHours(e.target.value)}
                placeholder="Ex.: 24, 72, 168"
                className="mt-2 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Quando definido, após cada publicação a próxima geração desta empresa é reagendada automaticamente para daqui a este número de horas — ideal para manter o perfil sempre em circulação. Deixe em branco para publicar apenas uma vez.
              </p>
            </label>
          </div>

          <div className="mt-4 rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/5 p-3 space-y-3">
            <label className="block">
              <span className="text-sm font-semibold text-foreground">🧩 Variações de palavras-chave (uma por linha)</span>
              <textarea
                rows={5}
                value={keywordVariations}
                onChange={(e) => setKeywordVariations(e.target.value)}
                placeholder={"Ex.:\nempresa X em São Paulo\nempresa X preços\nempresa X vale a pena"}
                className="mt-2 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                A cada agendamento executado, o sistema publica a próxima variação da lista, em ordem, e volta ao início ao terminar. Deixe vazio para usar sempre a palavra-chave principal.
              </p>
            </label>

            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">📆 Múltiplos agendamentos</span>
                <button
                  type="button"
                  onClick={() => setScheduledDates((arr) => [...arr, ""])}
                  className="rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary/60"
                >
                  + Adicionar data
                </button>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Cadastre várias datas/horas. Em cada execução do cron que atingir uma data, um artigo é gerado com a próxima variação da lista acima e a data é consumida.
              </p>
              <div className="mt-2 space-y-2">
                {scheduledDates.length === 0 && (
                  <p className="text-[11px] italic text-muted-foreground">Nenhuma data adicional agendada.</p>
                )}
                {scheduledDates.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="datetime-local"
                      value={d}
                      onChange={(e) => setScheduledDates((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))}
                      className="flex-1 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setScheduledDates((arr) => arr.filter((_, j) => j !== i))}
                      className="rounded-md border border-destructive/40 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </details>

        <div className="flex justify-end">
          <button type="submit" disabled={save.isPending} className="btn-primary whitespace-nowrap">
            {save.isPending
              ? (editingId ? "Salvando..." : "Adicionando...")
              : (editingId ? "Salvar alterações" : "Adicionar palavra-chave")}
          </button>
        </div>
        {save.error && <p className="text-sm text-destructive">{(save.error as Error).message}</p>}
      </form>


      {/* Mesclar palavras-chave existentes em uma nova (sem recadastrar produtos) */}
      <details className="card-glass">
        <summary className="cursor-pointer text-sm font-semibold">🔗 Mesclar palavras-chave em uma nova (comparativo)</summary>
        <div className="mt-3 space-y-3">
          <p className="text-xs text-muted-foreground">
            Selecione 2 ou mais palavras-chave já cadastradas. O produto da primeira vira o principal e os demais entram como produtos adicionais — pronto para gerar um review comparativo.
          </p>
          <div className="max-h-60 overflow-auto rounded-lg border border-border p-2">
            {(list.data ?? []).map((k: any) => (
              <label key={k.id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-secondary/40 text-sm">
                <input
                  type="checkbox"
                  checked={mergeIds.includes(k.id)}
                  onChange={(e) => setMergeIds((arr) => e.target.checked ? [...arr, k.id] : arr.filter((x) => x !== k.id))}
                />
                <span className="font-medium">{k.keyword}</span>
                {k.product_name && <span className="text-xs text-muted-foreground">— 🛒 {k.product_name}</span>}
                <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] capitalize">{k.article_type ?? "info"}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={mergeSelectedIntoForm} disabled={mergeIds.length < 2}
              className="btn-primary disabled:opacity-50">
              Mesclar {mergeIds.length > 0 ? `(${mergeIds.length})` : ""}
            </button>
            {mergeIds.length > 0 && (
              <button type="button" onClick={() => setMergeIds([])} className="text-xs text-muted-foreground hover:underline">
                Limpar seleção
              </button>
            )}
          </div>
        </div>
      </details>

      {/* Adicionar em massa: palavra-chave + ângulo, uma por linha */}
      <BulkAddKeywords onDone={() => qc.invalidateQueries({ queryKey: ["admin-keywords"] })} />

      {/* Filtros */}
      
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-background/40 px-4 py-3">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Filtrar por tipo:</label>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)}
          className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <option value="all">Todos</option>
          {ARTICLE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input
          type="text"
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          placeholder="Buscar palavra-chave, tópico ou produto..."
          className="flex-1 min-w-[220px] rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground"
        />
        {(typeFilter !== "all" || searchFilter) && (
          <button type="button" onClick={() => { setTypeFilter("all"); setSearchFilter(""); }}
            className="text-xs text-muted-foreground hover:underline">Limpar filtros</button>
        )}
      </div>

      {genError && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {genError}
        </div>
      )}

      {(() => {
        const norm = (s: unknown) => String(s ?? "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, " ")
          .trim();
        const collectSearchText = (value: unknown): string => {
          if (value == null) return "";
          if (Array.isArray(value)) return value.map(collectSearchText).join(" ");
          if (typeof value === "object") return Object.values(value as Record<string, unknown>).map(collectSearchText).join(" ");
          return String(value);
        };
        const q = norm(searchFilter);
        const terms = q ? q.split(/\s+/).filter(Boolean) : [];
        const filtered = (list.data ?? [])
          .filter((k: any) => typeFilter === "all" || String(k.article_type ?? "info").toLowerCase() === typeFilter)
          .filter((k: any) => {
            if (terms.length === 0) return true;
            const hay = [
              k.keyword,
              k.topic,
              k.article_type,
              k.product_name,
              k.product_url,
              k.product_image_alt,
              k.product_price,
              k.product_marketplace,
              collectSearchText(k.product_features),
              collectSearchText(k.extra_products),
              collectSearchText(k.business_data),
              collectSearchText(k.keyword_variations),
              collectSearchText(k.scheduled_dates),
            ].map(norm).join(" ");
            return terms.every((t) => hay.includes(t));
          });
        const total = list.data?.length ?? 0;
        return (
          <>
            {!list.isLoading && total > 0 && (
              <p className="text-xs text-muted-foreground">
                Mostrando <strong>{filtered.length}</strong> de <strong>{total}</strong> palavras-chave
                {(typeFilter !== "all" || searchFilter) ? " (filtradas)" : ""}.
              </p>
            )}
            <div className="overflow-hidden rounded-2xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Palavra-chave</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">Produto</th>
                    <th className="px-4 py-3">Último uso</th>
                    <th className="px-4 py-3">Usos</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {list.isLoading && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Carregando...</td></tr>}
                  {!list.isLoading && total === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Nenhuma palavra-chave cadastrada ainda.</td></tr>}
                  {!list.isLoading && total > 0 && filtered.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Nenhum resultado para os filtros aplicados.</td></tr>
                  )}
                  {filtered.map((k: any) => (
                    <tr key={k.id} className="border-t border-border">
                      <td className="px-4 py-3 font-medium">
                        {k.keyword}
                        {k.topic && <div className="text-xs text-muted-foreground">{k.topic}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className="rounded-full bg-secondary px-2 py-0.5 capitalize">{k.article_type ?? "info"}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {k.product_name ? (
                          <span title={k.product_url ?? ""}>🛒 {k.product_name}</span>
                        ) : "—"}
                        {Array.isArray(k.extra_products) && k.extra_products.length > 0 && (
                          <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                            +{k.extra_products.length} comparativo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{k.last_used_at ? formatDateBR(k.last_used_at) : "Nunca"}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex min-w-[2rem] justify-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {k.usage_count ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs ${k.active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                          {k.active ? "Ativa" : "Pausada"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => generateOne.mutate(k.id)}
                          disabled={genBusyId === k.id}
                          className="mr-3 rounded-md bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary hover:bg-primary/25 disabled:opacity-50"
                          title="Gerar e publicar um artigo agora usando esta palavra-chave"
                        >
                          {genBusyId === k.id ? "Gerando..." : "⚡ Gerar agora"}
                        </button>
                        <button onClick={() => loadKeywordIntoForm(k)} className="mr-3 text-foreground hover:underline">Editar</button>
                        <button onClick={() => duplicateKeywordIntoForm(k)} className="mr-3 text-foreground hover:underline">Duplicar</button>
                        <button onClick={() => toggle.mutate(k)} className="mr-3 text-primary hover:underline">{k.active ? "Pausar" : "Ativar"}</button>
                        <button onClick={() => { if (confirm(`Excluir "${k.keyword}"?`)) del.mutate(k.id); }} className="text-destructive hover:underline">Excluir</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}
    </div>
  );
}

function BulkAddKeywords({ onDone }: { onDone: () => void }) {
  const [text, setText] = useState("");
  const [articleType, setArticleType] = useState<"info" | "review" | "comparativo" | "melhores" | "empresa">("info");
  const [result, setResult] = useState<string | null>(null);

  function parseLines(): Array<{ keyword: string; topic: string; article_type: typeof articleType }> {
    return text
      .split(/\r?\n/)
      .map((raw) => raw.trim())
      .filter(Boolean)
      .map((line) => {
        // Accept "keyword | angle", "keyword ; angle", "keyword \t angle" or "keyword, angle" (first sep only).
        const m = line.match(/^([^|;\t]+?)\s*[|;\t]\s*(.+)$/) || line.match(/^([^,]+),\s*(.+)$/);
        const keyword = (m ? m[1] : line).trim();
        const topic = (m ? m[2] : "").trim();
        return { keyword, topic, article_type: articleType };
      })
      .filter((it) => it.keyword.length >= 2);
  }

  const items = parseLines();

  const bulk = useMutation({
    mutationFn: () => addKeywordsBulk({ data: { items } }),
    onSuccess: (r: any) => {
      setResult(`✅ ${r.inserted} adicionadas · ${r.skipped} ignoradas (duplicadas)`);
      setText("");
      onDone();
    },
    onError: (e: any) => setResult(`❌ ${e?.message ?? "Falha ao adicionar"}`),
  });

  return (
    <details className="card-glass" open>
      <summary className="cursor-pointer text-sm font-semibold">📥 Adicionar em massa (palavra-chave + ângulo)</summary>
      <div className="mt-3 space-y-3">
        <p className="text-xs text-muted-foreground">
          Cole uma <strong>por linha</strong>, no formato <code className="rounded bg-secondary/40 px-1">palavra-chave | ângulo</code>.
          Também aceita <code>;</code>, tab ou <code>,</code> como separador. O ângulo é opcional e vai para o campo <em>tópico/direção do artigo</em>.
        </p>
        <div className="grid gap-2 md:grid-cols-[1fr_240px]">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            placeholder={"reforma no bairro X | foco em obras públicas em atraso na cidade\ncomo trocar o registro de água | passo a passo faça você mesmo\nreview do fogão Y | comparativo entre modelos populares e preços"}
            className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 font-mono text-xs"
          />
          <div className="space-y-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tipo do artigo</label>
            <select
              value={articleType}
              onChange={(e) => setArticleType(e.target.value as any)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              {ARTICLE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground">
              Aplicado a todas as palavras-chave desta importação. Você pode editar o tipo de cada uma depois.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {items.length > 0 ? `${items.length} linha(s) válida(s) prontas para importar` : "Nenhuma linha detectada"}
          </span>
          <button
            type="button"
            disabled={items.length === 0 || bulk.isPending}
            onClick={() => { setResult(null); bulk.mutate(); }}
            className="btn-primary disabled:opacity-50"
          >
            {bulk.isPending ? "Adicionando..." : `Adicionar ${items.length || ""}`}
          </button>
        </div>
        {result && <p className="text-xs">{result}</p>}
      </div>
    </details>
  );
}

// ───────── Suporte CRM (IA + Playlist) ─────────

function SupportCrmTab() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["crm-settings"], queryFn: () => getCrmSettings() });
  const videos = useQuery({ queryKey: ["crm-videos"], queryFn: () => listCrmVideos() });

  const [instructions, setInstructions] = useState("");
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [autoSync, setAutoSync] = useState(true);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setInstructions((settings.data as any).support_instructions ?? "");
      setPlaylistUrl((settings.data as any).playlist_url ?? "");
      setAutoSync(Boolean((settings.data as any).auto_sync));
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      saveCrmSettings({
        data: { support_instructions: instructions, playlist_url: playlistUrl, auto_sync: autoSync },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-settings"] }),
  });

  const sync = useMutation({
    mutationFn: () => syncCrmVideos(),
    onSuccess: (res: any) => {
      if (res?.ok) {
        setSyncMsg(`Sincronização concluída: ${res.total} vídeos (${res.added} novos · ${res.updated} atualizados).`);
      } else {
        setSyncMsg(`Falha na sincronização: ${res?.error ?? "erro desconhecido"}`);
      }
      qc.invalidateQueries({ queryKey: ["crm-videos"] });
      qc.invalidateQueries({ queryKey: ["crm-settings"] });
    },
    onError: (err: any) => setSyncMsg(`Falha: ${err?.message ?? "erro"}`),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteCrmVideo({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-videos"] }),
  });

  const lastSync = (settings.data as any)?.last_sync_at as string | null | undefined;
  const lastError = (settings.data as any)?.last_sync_error as string | null | undefined;

  return (
    <div className="space-y-8">
      <div className="card-glass space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Instruções para a IA de Suporte</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Tudo que você escrever aqui será enviado como contexto adicional para a IA do chat de suporte do site Empresa Brasil Online.
            Use para orientar o tom editorial, temas prioritários (gestão, finanças, marketing, vendas, jurídico, tributação), critérios de
            recomendação de conteúdo e qualquer informação que ela precise saber sobre o portal.
          </p>
        </div>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={12}
          placeholder={"Ex.: O portal é sobre gestão de empresas e negócios no Brasil, com foco em empreendedores e pequenos/médios negócios. Priorize temas de MEI, fluxo de caixa, marketing digital para PMEs, contratação, tributação simples. Sempre recomende falar com um contador para decisões fiscais e indicar Sebrae/associações locais quando fizer sentido..."}
          className="w-full rounded-xl border border-border bg-background/60 p-3 text-sm outline-none focus:border-primary"
        />
        <div className="grid gap-4 md:grid-cols-[1fr_auto_auto] md:items-end">
          <div>
            <label className="text-xs font-medium text-muted-foreground">URL da playlist do YouTube</label>
            <input
              value={playlistUrl}
              onChange={(e) => setPlaylistUrl(e.target.value)}
              placeholder="https://www.youtube.com/playlist?list=..."
              className="mt-1 w-full rounded-xl border border-border bg-background/60 px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={autoSync} onChange={(e) => setAutoSync(e.target.checked)} />
            Sincronizar automaticamente
          </label>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary disabled:opacity-50">
            {save.isPending ? "Salvando…" : "Salvar configurações"}
          </button>
        </div>
        {save.isSuccess && <p className="text-xs text-emerald-400">Configurações salvas.</p>}
        {save.isError && <p className="text-xs text-destructive">Erro ao salvar.</p>}
      </div>

      <div className="card-glass space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">Sincronização da playlist</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              A IA usa esta lista como catálogo de tutoriais para enviar dentro do chat. Novos vídeos publicados na
              playlist são incorporados automaticamente (sincronização diária) — ou clique no botão para forçar agora.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Último sync: <strong>{lastSync ? formatDateBR(lastSync) : "nunca"}</strong>
              {lastError && <span className="ml-2 text-destructive">· {lastError}</span>}
            </p>
          </div>
          <button onClick={() => { setSyncMsg(null); sync.mutate(); }} disabled={sync.isPending} className="btn-primary disabled:opacity-50">
            {sync.isPending ? "Sincronizando…" : "Sincronizar agora"}
          </button>
        </div>
        {syncMsg && <p className="text-xs text-muted-foreground">{syncMsg}</p>}

        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Título</th>
                <th className="px-4 py-3">Categoria</th>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {(videos.data ?? []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Nenhum vídeo sincronizado. Clique em "Sincronizar agora".</td></tr>
              )}
              {(videos.data ?? []).map((v: any, i: number) => (
                <tr key={v.id} className="border-t border-border">
                  <td className="px-4 py-3 text-muted-foreground">{i + 1}</td>
                  <td className="px-4 py-3 font-medium">
                    <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener" className="hover:underline">{v.title}</a>
                  </td>
                  <td className="px-4 py-3">{v.category}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{v.id}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { if (confirm(`Remover "${v.title}"?`)) del.mutate(v.id); }} className="text-destructive hover:underline">Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ───────── Services CRUD ─────────

type ServiceRow = {
  id?: string; slug: string; label: string; description: string;
  icon: string; route_path: string; order_index: number; active: boolean;
};

const EMPTY_SERVICE: ServiceRow = {
  slug: "", label: "", description: "", icon: "✦", route_path: "", order_index: 0, active: true,
};

function ServicesTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-services"], queryFn: () => listServices() });
  const [draft, setDraft] = useState<ServiceRow>(EMPTY_SERVICE);
  const [editingId, setEditingId] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => saveService({ data: { ...draft, id: editingId ?? undefined } as any }),
    onSuccess: () => {
      setDraft(EMPTY_SERVICE); setEditingId(null);
      qc.invalidateQueries({ queryKey: ["admin-services"] });
    },
  });
  const toggle = useMutation({
    mutationFn: (s: any) => saveService({ data: { ...s, active: !s.active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-services"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteService({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-services"] }),
  });

  function edit(s: any) {
    setEditingId(s.id);
    setDraft({
      slug: s.slug, label: s.label, description: s.description ?? "",
      icon: s.icon ?? "", route_path: s.route_path ?? "",
      order_index: s.order_index ?? 0, active: s.active,
    });
  }
  function cancel() { setEditingId(null); setDraft(EMPTY_SERVICE); }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Cadastre aqui os serviços oferecidos pela empresa. Cada serviço criado fica disponível automaticamente como categoria no gerador de artigos, no filtro do blog e como opção no SEO Local.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
        className="card-glass grid gap-3 md:grid-cols-6">
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-muted-foreground">Nome do serviço</label>
          <input required value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-muted-foreground">Slug (URL)</label>
          <input required value={draft.slug}
            onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
            placeholder="ex: como-abrir-um-mei"
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Ícone</label>
          <input value={draft.icon} maxLength={4}
            onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Ordem</label>
          <input type="number" min={0} value={draft.order_index}
            onChange={(e) => setDraft({ ...draft, order_index: Number(e.target.value) || 0 })}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <div className="md:col-span-4">
          <label className="text-xs font-medium text-muted-foreground">Descrição curta</label>
          <textarea rows={2} value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-muted-foreground">URL/Rota (opcional)</label>
          <input value={draft.route_path}
            onChange={(e) => setDraft({ ...draft, route_path: e.target.value })}
            placeholder="ex: /blog"
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
        </div>
        <label className="md:col-span-3 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={draft.active}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
          Ativo (aparece nos filtros e no SEO Local)
        </label>
        <div className="md:col-span-3 flex items-center justify-end gap-2">
          {editingId && (
            <button type="button" onClick={cancel} className="rounded-lg border border-border px-3 py-2 text-sm">
              Cancelar
            </button>
          )}
          <button type="submit" disabled={save.isPending} className="btn-primary">
            {save.isPending ? "Salvando..." : editingId ? "Salvar alterações" : "Adicionar serviço"}
          </button>
        </div>
        {save.error && <p className="md:col-span-6 text-sm text-destructive">{(save.error as Error).message}</p>}
      </form>

      <div className="overflow-hidden rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Serviço</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Rota</th>
              <th className="px-4 py-3">Ordem</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Carregando...</td></tr>}
            {list.data?.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Nenhum serviço cadastrado.</td></tr>}
            {list.data?.map((s: any) => (
              <tr key={s.id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">
                  <span className="mr-2">{s.icon}</span>{s.label}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.slug}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.route_path}</td>
                <td className="px-4 py-3">{s.order_index}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${s.active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {s.active ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => edit(s)} className="mr-3 text-primary hover:underline">Editar</button>
                  <button onClick={() => toggle.mutate(s)} className="mr-3 text-primary hover:underline">{s.active ? "Desativar" : "Ativar"}</button>
                  <button onClick={() => { if (confirm(`Excluir "${s.label}"?`)) del.mutate(s.id); }} className="text-destructive hover:underline">Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────── Schedule ─────────

function ScheduleTab() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["admin-schedule"], queryFn: () => getScheduleSettings() });
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState<"daily" | "weekly" | "manual" | "interval">("daily");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [autoPublish, setAutoPublish] = useState(true);
  const [postsPerRun, setPostsPerRun] = useState(1);
  const [linksPerArticle, setLinksPerArticle] = useState(3);
  const [scheduleTimes, setScheduleTimes] = useState<string[]>(["08:00", "14:00", "20:00"]);
  const [useLinkBuilding, setUseLinkBuilding] = useState(true);
  const [intervalMinutes, setIntervalMinutes] = useState(0);
  const [rotateAuthors, setRotateAuthors] = useState(true);
  const [useVideosInSchedule, setUseVideosInSchedule] = useState(true);
  const [scheduleVideosPerArticle, setScheduleVideosPerArticle] = useState(1);
  const [imageSearchPrompt, setImageSearchPrompt] = useState("");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (settings.data && !hydrated) {
      setEnabled(settings.data.enabled);
      setFrequency((settings.data.frequency as any) ?? "daily");
      setSystemPrompt(settings.data.system_prompt ?? "");
      setAutoPublish(settings.data.auto_publish);
      setPostsPerRun((settings.data as any).posts_per_run ?? 1);
      setLinksPerArticle((settings.data as any).links_per_article ?? 3);
      const t = (settings.data as any).schedule_times as string[] | null;
      if (t && t.length) setScheduleTimes(t);
      if (typeof (settings.data as any).use_link_building === "boolean")
        setUseLinkBuilding((settings.data as any).use_link_building);
      setIntervalMinutes(Number((settings.data as any).interval_minutes ?? 0) || 0);
      setRotateAuthors((settings.data as any).rotate_authors !== false);
      if (typeof (settings.data as any).use_videos_in_schedule === "boolean")
        setUseVideosInSchedule((settings.data as any).use_videos_in_schedule);
      setScheduleVideosPerArticle(
        Math.max(0, Math.min(5, Number((settings.data as any).videos_per_article ?? 1) || 0)),
      );
      setImageSearchPrompt((settings.data as any).image_search_prompt ?? "");
      setHydrated(true);
    }
  }, [settings.data, hydrated]);

  const save = useMutation({
    mutationFn: () => saveScheduleSettings({ data: {
      enabled, frequency, system_prompt: systemPrompt, auto_publish: autoPublish,
      posts_per_run: postsPerRun, links_per_article: linksPerArticle,
      schedule_times: scheduleTimes, use_link_building: useLinkBuilding,
      interval_minutes: intervalMinutes,
      rotate_authors: rotateAuthors,
      use_videos_in_schedule: useVideosInSchedule,
      videos_per_article: scheduleVideosPerArticle,
      image_search_prompt: imageSearchPrompt.trim() || null,
    } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-schedule"] }),
  });

  function updateTime(i: number, v: string) {
    setScheduleTimes((arr) => arr.map((x, idx) => (idx === i ? v : x)));
  }
  function addTime() {
    if (scheduleTimes.length >= 6) return;
    setScheduleTimes((arr) => [...arr, "12:00"]);
  }
  function removeTime(i: number) {
    setScheduleTimes((arr) => (arr.length <= 1 ? arr : arr.filter((_, idx) => idx !== i)));
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="card-glass max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Geração automática ativada
        </label>
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} />
          Publicar artigos automaticamente
        </label>
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={useLinkBuilding} onChange={(e) => setUseLinkBuilding(e.target.checked)} />
          Usar link building automático
        </label>
        <label className="flex items-center gap-2 text-sm font-medium" title="Cada artigo gerado é atribuído ao integrante ativo da equipe (aba Equipe) menos usado, garantindo distribuição equilibrada de autoria.">
          <input type="checkbox" checked={rotateAuthors} onChange={(e) => setRotateAuthors(e.target.checked)} />
          Rodízio de autores da equipe
        </label>
        <label className="flex items-center gap-2 text-sm font-medium" title="Quando desligado, os artigos agendados são gerados sem nenhum vídeo do YouTube, mesmo que existam links na biblioteca de vídeos padrão.">
          <input type="checkbox" checked={useVideosInSchedule} onChange={(e) => setUseVideosInSchedule(e.target.checked)} />
          Usar vídeos nos artigos agendados
        </label>
        <label className="flex items-center gap-2 text-sm font-medium" title="Quantos vídeos da biblioteca o agente vai inserir em cada artigo gerado pelo agendamento.">
          Vídeos por artigo
          <select
            value={scheduleVideosPerArticle}
            onChange={(e) => setScheduleVideosPerArticle(Number(e.target.value))}
            disabled={!useVideosInSchedule}
            className="rounded-lg border border-border bg-background/60 px-2 py-1 text-sm disabled:opacity-50"
          >
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
      </div>
      <div>
        <label className="text-sm font-medium">Frequência</label>
        <select value={frequency} onChange={(e) => setFrequency(e.target.value as any)}
          className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm">
          <option value="daily">Diária (executa nos horários abaixo)</option>
          <option value="weekly">Semanal (segundas, nos horários abaixo)</option>
          <option value="interval">Intervalo (a cada X minutos)</option>
          <option value="manual">Manual (não roda automaticamente)</option>
        </select>
        <p className="mt-1 text-xs text-muted-foreground">No modo "Intervalo" o sistema ignora os horários fixos e gera um novo artigo a cada X minutos definidos abaixo. O cron do banco roda a cada 5 minutos — o intervalo efetivo será arredondado para o múltiplo mais próximo.</p>
      </div>
      {frequency === "interval" && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-3">
          <label className="text-sm font-medium">Intervalo entre artigos (minutos)</label>
          <input
            type="number" min={5} max={1440} step={5} value={intervalMinutes}
            onChange={(e) => setIntervalMinutes(Math.max(0, Math.min(1440, Number(e.target.value) || 0)))}
            className="mt-1 w-40 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Ex.: 60 = um artigo por hora · 30 = a cada 30 min · 240 = a cada 4 horas. Mínimo prático: 5 minutos.
          </p>
        </div>
      )}
      <div className="rounded-xl border border-border p-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Horários de execução (BRT)</label>
          <button type="button" onClick={addTime} className="text-xs text-primary hover:underline">+ adicionar horário</button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Ex.: 08:00 (manhã), 14:00 (tarde), 20:00 (noite). Cada horário roda independentemente e gera <strong>{postsPerRun}</strong> artigo(s) por execução.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {scheduleTimes.map((t, i) => (
            <div key={i} className="flex items-center gap-1 rounded-lg border border-border bg-background/60 px-2 py-1">
              <input type="time" value={t} onChange={(e) => updateTime(i, e.target.value)} className="bg-transparent text-sm" />
              {scheduleTimes.length > 1 && (
                <button type="button" onClick={() => removeTime(i)} className="text-xs text-destructive hover:underline" title="Remover horário">×</button>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="text-sm font-medium">Artigos por execução</label>
          <input type="number" min={1} max={10} value={postsPerRun}
            onChange={(e) => setPostsPerRun(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          <p className="mt-1 text-xs text-muted-foreground">Aumente para gerar mais artigos a cada rodada do cron.</p>
        </div>
        <div>
          <label className="text-sm font-medium">Links internos por artigo</label>
          <input type="number" min={0} max={15} value={linksPerArticle}
            onChange={(e) => setLinksPerArticle(Math.max(0, Math.min(15, Number(e.target.value) || 0)))}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          <p className="mt-1 text-xs text-muted-foreground">Aplicado apenas quando "Usar link building automático" estiver ligado. O sistema busca no sitemap as URLs com melhor correspondência por palavra-chave e insere automaticamente.</p>
        </div>
      </div>
      <div>
        <label className="text-sm font-medium">Prompt do redator (System Prompt do GPT)</label>
        <textarea rows={10} value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)}
          className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
      </div>
      <div>
        <label className="text-sm font-medium">Prompt de busca de imagens (Pexels)</label>
        <textarea rows={4} value={imageSearchPrompt} onChange={(e) => setImageSearchPrompt(e.target.value)}
          placeholder="Ex.: brazilian entrepreneur, small business owner, office team meeting, business strategy planning, coworking space professionals. Use termos em inglês separados por vírgula — cada termo vira uma consulta no Pexels."
          className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
        <p className="mt-1 text-xs text-muted-foreground">Descreva (em inglês, separado por vírgulas) os tipos de imagem que você quer encontrar no Pexels. O sistema tenta cada termo até achar uma imagem inédita relacionada ao nicho.</p>
      </div>
      {save.error && <p className="text-sm text-destructive">{(save.error as Error).message}</p>}
      {save.isSuccess && <p className="text-sm text-primary">Configurações salvas.</p>}
      <div className="flex justify-end">
        <button type="submit" disabled={save.isPending} className="btn-primary">{save.isPending ? "Salvando..." : "Salvar"}</button>
      </div>
    </form>
  );
}

// ───────── Internal links ─────────

function LinksTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-links"], queryFn: () => listInternalLinks() });
  const [anchor, setAnchor] = useState("");
  const [url, setUrl] = useState("");
  const [priority, setPriority] = useState(5);
  const save = useMutation({
    mutationFn: () => saveInternalLink({ data: { anchor: anchor.trim(), url: url.trim(), active: true, priority } }),
    onSuccess: () => { setAnchor(""); setUrl(""); setPriority(5); qc.invalidateQueries({ queryKey: ["admin-links"] }); },
  });
  const toggle = useMutation({
    mutationFn: (l: any) => saveInternalLink({ data: { id: l.id, anchor: l.anchor, url: l.url, active: !l.active, priority: l.priority } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-links"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteInternalLink({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-links"] }),
  });
  const importXml = useMutation({
    mutationFn: () => importSitemapLinks(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-links"] }),
  });
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const sitemapUrl = `${origin}/sitemap-index.xml`;
  const [copied, setCopied] = useState(false);
  const copySitemap = async () => {
    try {
      await navigator.clipboard.writeText(sitemapUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-secondary/40 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Sitemap XML para o Search Console</p>
            <p className="mt-1 text-xs text-muted-foreground max-w-2xl">
              Copie o link abaixo e cole em <strong>Google Search Console → Sitemaps</strong> (ou em outras ferramentas de indexação) para que os buscadores rastreiem todas as páginas do site.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href="/sitemap-index.xml" target="_blank" rel="noopener" className="btn-ghost whitespace-nowrap">Abrir XML</a>
            <button onClick={copySitemap} className="btn-primary whitespace-nowrap">
              {copied ? "Link copiado!" : "Copiar link do XML"}
            </button>
          </div>
        </div>
        <code className="mt-3 block w-full overflow-x-auto rounded-md border border-border bg-background/60 px-3 py-2 text-xs text-foreground">
          {sitemapUrl || "/sitemap-index.xml"}
        </code>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-3xl">
          Cada regra abaixo é aplicada automaticamente aos artigos do blog: a primeira ocorrência de cada âncora vira um link interno apontando para a URL definida. Use isso para construir link building entre páginas do site.
        </p>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => importXml.mutate()}
            disabled={importXml.isPending}
            className="btn-primary whitespace-nowrap"
          >
            {importXml.isPending ? "Importando..." : "Importar XML do site"}
          </button>
          {importXml.data && (
            <p className="text-xs text-muted-foreground">
              {(importXml.data as any).inserted} novos / {(importXml.data as any).scanned} encontrados
            </p>
          )}
          {importXml.error && <p className="text-xs text-destructive">{(importXml.error as Error).message}</p>}
        </div>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); if (anchor.trim() && url.trim()) save.mutate(); }}
        className="card-glass grid gap-3 md:grid-cols-[2fr_2fr_auto_auto]">
        <input required value={anchor} onChange={(e) => setAnchor(e.target.value)}
          placeholder='Âncora (ex: "SEO local")'
          className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        <input required value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="/blog ou https://..."
          className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
        <input type="number" min={0} max={100} value={priority} onChange={(e) => setPriority(Number(e.target.value))}
          className="w-24 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" title="Prioridade" />
        <button type="submit" disabled={save.isPending} className="btn-primary whitespace-nowrap">{save.isPending ? "..." : "Adicionar"}</button>
        {save.error && <p className="md:col-span-4 text-sm text-destructive">{(save.error as Error).message}</p>}
      </form>
      <div className="overflow-hidden rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Âncora</th>
              <th className="px-4 py-3">Destino</th>
              <th className="px-4 py-3">Prioridade</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Carregando...</td></tr>}
            {list.data?.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Nenhum link cadastrado.</td></tr>}
            {list.data?.map((l: any) => (
              <tr key={l.id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{l.anchor}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{l.url}</td>
                <td className="px-4 py-3">{l.priority}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${l.active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {l.active ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => toggle.mutate(l)} className="mr-3 text-primary hover:underline">{l.active ? "Desativar" : "Ativar"}</button>
                  <button onClick={() => { if (confirm(`Excluir "${l.anchor}"?`)) del.mutate(l.id); }} className="text-destructive hover:underline">Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────── Generate (manual trigger) ─────────

function GenerateTab() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const [selectedKeywordIds, setSelectedKeywordIds] = useState<string[]>([]);
  const [topic, setTopic] = useState("");
  const [youtube, setYoutube] = useState("");
  const [cover, setCover] = useState("");
  const [category, setCategory] = useState("");
  const [autoPublish, setAutoPublish] = useState(true);
  const [linksToInsert, setLinksToInsert] = useState(3);
  const [useLinkBuilding, setUseLinkBuilding] = useState(true);
  const [batchCount, setBatchCount] = useState(1);
  const [batchResult, setBatchResult] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const servicesQ = useQuery({ queryKey: ["admin-services"], queryFn: () => listServices() });
  const keywordsQ = useQuery({ queryKey: ["admin-keywords"], queryFn: () => listKeywords() });
  const keywordsList = (keywordsQ.data ?? []) as Array<any>;
  const productKeywordsSelected = selectedKeywordIds.filter((id) => {
    const k = keywordsList.find((x) => x.id === id);
    return k && k.product_name && k.product_url;
  });
  const isComparativeMode = productKeywordsSelected.length >= 2;
  const DEFAULT_CATS = ["Gestão Empresarial", "Finanças & Controladoria", "Marketing & Vendas", "Pessoas & Liderança", "Jurídico & Tributário", "Empreendedorismo", "Operações & Processos", "Estratégia & Crescimento"];
  const CATEGORIES = Array.from(new Set([...(servicesQ.data ?? []).map((s: any) => s.label), ...DEFAULT_CATS]));

  // System prompt (shared by manual, batch and scheduled generation)
  const settingsQ = useQuery({ queryKey: ["admin-schedule"], queryFn: () => getScheduleSettings() });
  const [systemPrompt, setSystemPrompt] = useState("");
  const [promptHydrated, setPromptHydrated] = useState(false);
  const [videoRows, setVideoRows] = useState<Array<{ url: string; title: string }>>([]);
  const [videosPerArticle, setVideosPerArticle] = useState(1);
  const [channelUrl, setChannelUrl] = useState("");
  const [channelHydrated, setChannelHydrated] = useState(false);
  useEffect(() => {
    if (settingsQ.data && !promptHydrated) {
      setSystemPrompt(settingsQ.data.system_prompt ?? "");
      const urls = ((settingsQ.data as any).default_youtube_urls as string[] | null | undefined) ?? [];
      const titles = ((settingsQ.data as any).default_youtube_titles as string[] | null | undefined) ?? [];
      const rows = urls.map((u, i) => ({ url: u, title: titles[i] ?? "" }));
      setVideoRows(rows.length ? rows : [{ url: "", title: "" }]);
      const vpa = Number((settingsQ.data as any).videos_per_article ?? 1);
      setVideosPerArticle(Number.isFinite(vpa) ? Math.max(0, Math.min(5, vpa)) : 1);
      setPromptHydrated(true);
    }
  }, [settingsQ.data, promptHydrated]);
  useEffect(() => {
    if (settingsQ.data && !channelHydrated) {
      setChannelUrl(((settingsQ.data as any).youtube_channel_url as string | null) ?? "");
      setChannelHydrated(true);
    }
  }, [settingsQ.data, channelHydrated]);
  const syncChannel = useMutation({
    mutationFn: () => syncYoutubeChannel({ data: { channelUrl: channelUrl.trim() } }),
    onSuccess: async () => {
      const fresh = await qc.fetchQuery({ queryKey: ["admin-schedule"], queryFn: () => getScheduleSettings() });
      const urls = ((fresh as any)?.default_youtube_urls as string[] | null | undefined) ?? [];
      const titles = ((fresh as any)?.default_youtube_titles as string[] | null | undefined) ?? [];
      setVideoRows(urls.map((u, i) => ({ url: u, title: titles[i] ?? "" })));
    },
  });
  const savePrompt = useMutation({
    mutationFn: () => {
      const cleaned = videoRows
        .map((r) => ({ url: r.url.trim(), title: r.title.trim() }))
        .filter((r) => r.url);
      return saveSystemPrompt({
        data: {
          system_prompt: systemPrompt,
          default_youtube_urls: cleaned.map((r) => r.url).slice(0, 50),
          default_youtube_titles: cleaned.map((r) => r.title).slice(0, 50),
          videos_per_article: videosPerArticle,
        },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-schedule"] }),
  });

  const run = useMutation({
    mutationFn: () => generatePostNow({
      data: {
        keyword: keyword.trim() || undefined,
        keywordIds: selectedKeywordIds.length > 0 ? selectedKeywordIds : undefined,
        topic: topic.trim() || undefined,
        youtubeUrls: youtube.split(/\s+/).map((s) => s.trim()).filter(Boolean),
        coverImageOverride: cover.trim() || null,
        autoPublish,
        category: category.trim() || undefined,
        linksToInsert: useLinkBuilding ? linksToInsert : 0,
        article_type: isComparativeMode ? "review" : undefined,
      },
    }),
    onSuccess: (r) => {
      setResult(`Artigo criado: ${r.title}`);
      setTimeout(() => navigate({ to: "/admin/post/$id", params: { id: r.id } }), 800);
    },
  });

  const batch = useMutation({
    mutationFn: () => generatePostsBatch({ data: {
      count: batchCount, autoPublish,
      linksToInsert: useLinkBuilding ? linksToInsert : 0,
      useLinkBuilding,
      category: category.trim() || undefined,
      keyword: keyword.trim() || undefined,
      topic: topic.trim() || undefined,
    } }),
    onSuccess: (rs: any) => {
      const ok = rs.filter((r: any) => r.ok).length;
      setBatchResult(`${ok}/${rs.length} artigos criados.`);
    },
  });

  return (
    <div className="max-w-3xl space-y-6">
      <div className="card-glass space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Prompt do redator (IA)</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Este prompt é usado em <strong>todas as gerações</strong> de artigos:
            criação manual, em lote e agendada. Edite aqui para padronizar tom,
            estrutura e diretrizes do redator.
          </p>
        </div>
        <textarea
          rows={8}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder="Você é um redator SEO brasileiro..."
          className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono"
        />
        <div className="space-y-2">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <label className="text-sm font-medium">Biblioteca de vídeos do YouTube</label>
              <p className="mt-1 text-xs text-muted-foreground">
                Preencha o <strong>título</strong> de cada vídeo junto com o link. A IA
                escolhe os vídeos pelo título — sem ele, o sistema acabava
                colocando sempre o mesmo vídeo em todos os artigos.
                Máx. 50 vídeos.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium whitespace-nowrap">Vídeos por artigo</label>
              <input
                type="number"
                min={0}
                max={5}
                value={videosPerArticle}
                onChange={(e) => setVideosPerArticle(Math.max(0, Math.min(5, Number(e.target.value) || 0)))}
                className="w-16 rounded-lg border border-border bg-background/60 px-2 py-1 text-sm text-center"
              />
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-background/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 text-left">Título</th>
                  <th className="px-2 py-1 text-left">Link do YouTube</th>
                  <th className="px-2 py-1 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {videoRows.map((row, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-1">
                      <input
                        value={row.title}
                        onChange={(e) => {
                          const v = e.target.value;
                          setVideoRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, title: v } : r)));
                        }}
                        placeholder="Ex: Como configurar o Google Meu Negócio"
                        className="w-full rounded border border-transparent bg-background/40 px-2 py-1 text-sm focus:border-border"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        value={row.url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setVideoRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, url: v } : r)));
                        }}
                        placeholder="https://youtu.be/..."
                        className="w-full rounded border border-transparent bg-background/40 px-2 py-1 font-mono text-xs focus:border-border"
                      />
                    </td>
                    <td className="p-1 text-center">
                      <button
                        type="button"
                        onClick={() => setVideoRows((rows) => rows.filter((_, idx) => idx !== i))}
                        className="text-xs text-muted-foreground hover:text-destructive"
                        aria-label="Remover vídeo"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={() => setVideoRows((rows) => (rows.length >= 50 ? rows : [...rows, { url: "", title: "" }]))}
            className="rounded-lg border border-border px-3 py-1 text-xs hover:bg-background/60"
          >
            + Adicionar vídeo
          </button>
        </div>
        <div className="rounded-lg border border-border bg-background/40 p-3 space-y-2">
          <div>
            <label className="text-sm font-medium">Sincronizar canal do YouTube</label>
            <p className="mt-1 text-xs text-muted-foreground">
              Cole a URL do canal (ex.: <code className="text-[10px]">https://www.youtube.com/@seucanal</code>)
              ou de uma playlist. O sistema importa os vídeos mais recentes com título e link
              já preenchidos na biblioteca acima.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="url"
              value={channelUrl}
              onChange={(e) => setChannelUrl(e.target.value)}
              placeholder="https://www.youtube.com/@seucanal"
              className="flex-1 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono"
            />
            <button
              type="button"
              disabled={syncChannel.isPending || channelUrl.trim().length < 10}
              onClick={() => syncChannel.mutate()}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {syncChannel.isPending ? "Sincronizando…" : "Sincronizar agora"}
            </button>
          </div>
          <div className="text-xs">
            {syncChannel.isSuccess && (
              <span className="text-primary">
                {syncChannel.data?.total} vídeos importados.
              </span>
            )}
            {syncChannel.error && (
              <span className="text-destructive">{(syncChannel.error as Error).message}</span>
            )}
            {!syncChannel.isPending && !syncChannel.isSuccess && !syncChannel.error && (settingsQ.data as any)?.last_channel_sync_at && (
              <span className="text-muted-foreground">
                Última sincronização: {formatDateBR((settingsQ.data as any).last_channel_sync_at)}
                {(settingsQ.data as any).last_channel_sync_count != null && ` · ${(settingsQ.data as any).last_channel_sync_count} vídeos`}
                {(settingsQ.data as any).last_channel_sync_error && ` · erro: ${(settingsQ.data as any).last_channel_sync_error}`}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs">
            {savePrompt.isSuccess && <span className="text-primary">Configurações salvas.</span>}
            {savePrompt.error && <span className="text-destructive">{(savePrompt.error as Error).message}</span>}
          </div>
          <button
            type="button"
            disabled={savePrompt.isPending || systemPrompt.trim().length < 20}
            onClick={() => savePrompt.mutate()}
            className="rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
          >
            {savePrompt.isPending ? "Salvando..." : "Salvar prompt e vídeos"}
          </button>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); setResult(null); run.mutate(); }} className="card-glass space-y-4">
        <div>
        <p className="text-sm text-muted-foreground">
          Gera um artigo completo via GPT, busca imagem de capa no Pexels e aplica link building automático ao publicar. Deixe a palavra-chave em branco para usar a próxima da fila.
        </p>
      </div>
      <div className="space-y-2 rounded-xl border border-border bg-background/40 p-4">
        <div className="flex items-center justify-between gap-2">
          <label className="text-sm font-medium">Palavras-chave cadastradas</label>
          <span className="text-xs text-muted-foreground">
            {selectedKeywordIds.length} selecionada{selectedKeywordIds.length === 1 ? "" : "s"}
            {isComparativeMode && " — modo REVIEW COMPARATIVO"}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Escolha 1 palavra-chave para gerar um artigo focado. Selecione <strong>2 ou mais palavras-chave com produto afiliado</strong> para gerar automaticamente um <strong>review comparativo</strong> com tabela, prós/contras e card por produto. Deixe nada selecionado para usar a próxima da fila.
        </p>
        <div className="max-h-56 overflow-y-auto rounded-lg border border-border bg-background/60 p-2 text-sm">
          {keywordsList.length === 0 ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">Nenhuma palavra-chave cadastrada ainda.</p>
          ) : (
            keywordsList.map((k: any) => {
              const checked = selectedKeywordIds.includes(k.id);
              const hasProd = Boolean(k.product_name && k.product_url);
              return (
                <label key={k.id} className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 hover:bg-background/80">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={checked}
                    onChange={(e) => {
                      setSelectedKeywordIds((prev) =>
                        e.target.checked ? [...prev, k.id] : prev.filter((x) => x !== k.id),
                      );
                    }}
                  />
                  <span className="flex-1">
                    <span className="font-medium text-foreground">{k.keyword}</span>
                    {hasProd && (
                      <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">
                        produto: {k.product_name}
                      </span>
                    )}
                    {k.article_type && k.article_type !== "info" && (
                      <span className="ml-2 text-[10px] uppercase text-muted-foreground">{k.article_type}</span>
                    )}
                  </span>
                </label>
              );
            })
          )}
        </div>
        {selectedKeywordIds.length > 0 && (
          <button
            type="button"
            onClick={() => setSelectedKeywordIds([])}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Limpar seleção
          </button>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="text-sm font-medium">
            Palavra-chave manual (opcional){selectedKeywordIds.length > 0 && " — ignorada quando há seleção acima"}
          </label>
          <input value={keyword} onChange={(e) => setKeyword(e.target.value)}
            disabled={selectedKeywordIds.length > 0}
            placeholder="Digite uma palavra-chave avulsa"
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm disabled:opacity-50" />
        </div>
        <div>
          <label className="text-sm font-medium">Capa (URL, opcional)</label>
          <input value={cover} onChange={(e) => setCover(e.target.value)}
            placeholder="Deixe em branco para Pexels automático"
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
        </div>
      </div>
      <div>
        <label className="text-sm font-medium">Categoria</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm">
          <option value="">Automática (definida pela IA)</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="text-sm font-medium">Links internos a inserir</label>
          <input type="number" min={0} max={15} value={linksToInsert}
            onChange={(e) => setLinksToInsert(Math.max(0, Math.min(15, Number(e.target.value) || 0)))}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          <p className="mt-1 text-xs text-muted-foreground">O sistema escolhe automaticamente os melhores destinos do sitemap por correspondência de palavra-chave.</p>
        </div>
        <label className="mt-6 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={useLinkBuilding} onChange={(e) => setUseLinkBuilding(e.target.checked)} />
          Usar link building automático nesta geração
        </label>
      </div>
      <div>
        <label className="text-sm font-medium">Assunto / ângulo (opcional)</label>
        <textarea rows={2} value={topic} onChange={(e) => setTopic(e.target.value)}
          className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="text-sm font-medium">URLs do YouTube (separadas por espaço, opcional)</label>
        <textarea rows={2} value={youtube} onChange={(e) => setYoutube(e.target.value)}
          placeholder="https://youtu.be/..."
          className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} />
        Publicar imediatamente
      </label>
      {run.error && <p className="text-sm text-destructive">{(run.error as Error).message}</p>}
      {result && <p className="text-sm text-primary">{result}</p>}
      <div className="flex justify-end">
        <button type="submit" disabled={run.isPending} className="btn-primary">
          {run.isPending ? "Gerando (pode levar 30s)..." : "Gerar artigo agora"}
        </button>
      </div>
      <div className="rounded-2xl border border-border/60 p-4 bg-background/40">
        <h3 className="text-sm font-semibold">Geração em lote (alta frequência)</h3>
        <p className="mt-1 text-xs text-muted-foreground">Gera vários artigos em sequência usando as palavras-chave da fila. Use a categoria selecionada acima como filtro opcional.</p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium">Quantidade</label>
            <input type="number" min={1} max={10} value={batchCount}
              onChange={(e) => setBatchCount(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
              className="mt-1 w-24 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
          <button type="button" disabled={batch.isPending}
            onClick={() => { setBatchResult(null); batch.mutate(); }}
            className="rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10">
            {batch.isPending ? `Gerando ${batchCount}...` : `Gerar ${batchCount} artigos`}
          </button>
          {batchResult && <span className="text-sm text-primary">{batchResult}</span>}
          {batch.error && <span className="text-sm text-destructive">{(batch.error as Error).message}</span>}
        </div>
      </div>
      </form>
    </div>
  );
}

// ───────── Videos ─────────

function VideosTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-videos"], queryFn: () => listAllVideos() });
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [thumb, setThumb] = useState("");
  const [order, setOrder] = useState(0);

  const save = useMutation({
    mutationFn: () => saveVideo({ data: {
      title: title.trim(), url: url.trim(), description: description.trim(),
      thumbnail_url: thumb.trim() || null, order_index: order, published: true,
    } }),
    onSuccess: () => {
      setTitle(""); setUrl(""); setDescription(""); setThumb(""); setOrder(0);
      qc.invalidateQueries({ queryKey: ["admin-videos"] });
    },
  });
  const togglePub = useMutation({
    mutationFn: (v: any) => saveVideo({ data: {
      id: v.id, title: v.title, url: v.url, description: v.description ?? "",
      thumbnail_url: v.thumbnail_url, order_index: v.order_index, published: !v.published,
    } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-videos"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteVideo({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-videos"] }),
  });

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Cadastre vídeos (YouTube) que aparecerão logo abaixo dos artigos no blog como conteúdo criado.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); if (title.trim() && url.trim()) save.mutate(); }}
        className="card-glass grid gap-3 md:grid-cols-2">
        <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título do vídeo"
          className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        <input required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="URL do YouTube"
          className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
        <input value={thumb} onChange={(e) => setThumb(e.target.value)} placeholder="Thumbnail (URL opcional)"
          className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
        <input type="number" min={0} value={order} onChange={(e) => setOrder(Number(e.target.value))}
          placeholder="Ordem" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Descrição (opcional)"
          className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        <div className="md:col-span-2 flex justify-end">
          <button type="submit" disabled={save.isPending} className="btn-primary">{save.isPending ? "Salvando..." : "Adicionar vídeo"}</button>
        </div>
        {save.error && <p className="md:col-span-2 text-sm text-destructive">{(save.error as Error).message}</p>}
      </form>
      <div className="overflow-hidden rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Título</th>
              <th className="px-4 py-3">URL</th>
              <th className="px-4 py-3">Ordem</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Carregando...</td></tr>}
            {list.data?.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Nenhum vídeo cadastrado.</td></tr>}
            {list.data?.map((v: any) => (
              <tr key={v.id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{v.title}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground"><a href={v.url} target="_blank" rel="noopener" className="hover:underline">{v.url}</a></td>
                <td className="px-4 py-3">{v.order_index}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${v.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {v.published ? "Publicado" : "Oculto"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => togglePub.mutate(v)} className="mr-3 text-primary hover:underline">{v.published ? "Ocultar" : "Publicar"}</button>
                  <button onClick={() => { if (confirm(`Excluir "${v.title}"?`)) del.mutate(v.id); }} className="text-destructive hover:underline">Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────── SEO Local (Service Locations) ─────────

const LOCAL_SERVICES_FALLBACK = [
  { slug: "abertura-de-empresa", label: "Abertura de Empresa (MEI, ME, LTDA)" },
  { slug: "contabilidade-para-pequenas-empresas", label: "Contabilidade para Pequenas Empresas" },
  { slug: "marketing-digital-para-empresas", label: "Marketing Digital para Empresas" },
  { slug: "gestao-financeira-para-pmes", label: "Gestão Financeira para PMEs" },
  { slug: "consultoria-para-empreendedores", label: "Consultoria para Empreendedores" },
  { slug: "vendas-e-atendimento-para-negocios-locais", label: "Vendas e Atendimento para Negócios Locais" },
];

function LocalSeoPromptEditor() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["admin-schedule"], queryFn: () => getScheduleSettings() });
  const [prompt, setPrompt] = useState("");
  const [open, setOpen] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  useEffect(() => {
    if (settings.data && (settings.data as any).local_seo_system_prompt != null) {
      setPrompt((settings.data as any).local_seo_system_prompt ?? "");
    }
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () => saveLocalSeoPrompt({ data: { local_seo_system_prompt: prompt.trim() } }),
    onSuccess: () => {
      setSavedMsg("Prompt salvo.");
      qc.invalidateQueries({ queryKey: ["admin-schedule"] });
      setTimeout(() => setSavedMsg(null), 2500);
    },
  });
  return (
    <div className="card-glass">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <h3 className="text-sm font-semibold">Prompt do agente de SEO Local</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Persona e regras enviadas como <code>system</code> ao modelo. Foco em empreendedores e gestores de pequenos e médios negócios no Brasil.
          </p>
        </div>
        <span className="text-xs text-muted-foreground">{open ? "Recolher" : "Editar"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <textarea
            rows={10}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Você é um redator SEO especializado em varejo local para o consumidor final..."
            className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono"
          />
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs">
              {save.error && <span className="text-destructive">{(save.error as Error).message}</span>}
              {savedMsg && <span className="text-primary">{savedMsg}</span>}
            </div>
            <button
              type="button"
              disabled={save.isPending || prompt.trim().length < 20}
              onClick={() => save.mutate()}
              className="btn-primary disabled:opacity-50"
            >
              {save.isPending ? "Salvando..." : "Salvar prompt"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LocationsTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-locations"], queryFn: () => listAllLocations() });
  const services = useQuery({ queryKey: ["admin-services"], queryFn: () => listServices() });
  const available = (services.data && services.data.length > 0)
    ? services.data.map((s: any) => ({ slug: s.slug, label: s.label }))
    : LOCAL_SERVICES_FALLBACK;
  const [serviceSlug, setServiceSlug] = useState<string>(available[0]?.slug ?? "abertura-de-empresa");
  useEffect(() => {
    if (services.data && services.data.length > 0 && !services.data.find((s: any) => s.slug === serviceSlug)) {
      setServiceSlug(services.data[0].slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services.data]);
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [autoPublish, setAutoPublish] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [bulk, setBulk] = useState("");
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);
  const [topicInstruction, setTopicInstruction] = useState("");
  const [useCustomKeyword, setUseCustomKeyword] = useState(false);
  const [customKeyword, setCustomKeyword] = useState("");

  function slugifyLocal(v: string) {
    return v.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 80);
  }

  function resolveServiceForRun() {
    if (useCustomKeyword && customKeyword.trim()) {
      const label = customKeyword.trim();
      return { slug: slugifyLocal(label) || "palavra-chave", label };
    }
    const svc = available.find((s) => s.slug === serviceSlug)!;
    return { slug: svc.slug, label: svc.label };
  }

  const gen = useMutation({
    mutationFn: () => {
      const svc = resolveServiceForRun();
      return generateLocationPage({ data: {
        service_slug: svc.slug, service_label: svc.label,
        city: city.trim(), neighborhood: neighborhood.trim() || undefined,
        topic_instruction: topicInstruction.trim() || undefined,
        autoPublish,
      } });
    },
    onSuccess: (r) => {
      setMsg(`Página criada: ${r.title}`);
      setCity(""); setNeighborhood("");
      qc.invalidateQueries({ queryKey: ["admin-locations"] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteLocation({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-locations"] }),
  });
  const bulkGen = useMutation({
    mutationFn: () => {
      const svc = resolveServiceForRun();
      return generateLocationsBatch({ data: {
        service_slug: svc.slug, service_label: svc.label,
        locations: bulk,
        topic_instruction: topicInstruction.trim() || undefined,
        autoPublish,
      } });
    },
    onSuccess: (rows: any) => {
      const ok = rows.filter((r: any) => r.ok).length;
      setBulkMsg(`${ok}/${rows.length} páginas criadas.`);
      setBulk("");
      qc.invalidateQueries({ queryKey: ["admin-locations"] });
      qc.invalidateQueries({ queryKey: ["local-seo-progress"] });
    },
  });

  // Live progress for the bulk generator — polled while a run is in flight
  // (and for ~30s after) so the admin can see the system is actively working.
  // Polling só enquanto um batch está de fato rodando; parado = sem consultas
  // repetidas. Antes ficava perguntando o progresso a cada 8s indefinidamente.
  const progress = useQuery({
    queryKey: ["local-seo-progress"],
    queryFn: () => getLocalSeoBatchProgress(),
    refetchInterval: bulkGen.isPending ? 2000 : false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
  const p = progress.data;
  const isRunning = p?.local_seo_batch_status === "running" || bulkGen.isPending;
  const total = p?.local_seo_batch_total ?? 0;
  const done = p?.local_seo_batch_done ?? 0;
  const failed = p?.local_seo_batch_failed ?? 0;
  const skippedCount = p?.local_seo_batch_skipped ?? 0;
  const processedCount = p?.local_seo_batch_processed ?? 0;
  const history = Array.isArray(p?.local_seo_batch_history) ? p!.local_seo_batch_history! : [];
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  const regenOne = useMutation({
    mutationFn: (id: string) => regenerateLocationPage({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-locations"] }),
  });
  const regenThin = useMutation({
    mutationFn: () => regenerateThinLocations({ data: { minLength: 800 } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-locations"] }),
  });
  const continueBatch = useMutation({
    mutationFn: () => continueLocationsBatch(),
    onSuccess: (rows: any) => {
      const ok = Array.isArray(rows) ? rows.filter((r: any) => r.ok && !r.skipped).length : 0;
      const skipped = Array.isArray(rows) ? rows.filter((r: any) => r.skipped).length : 0;
      setBulkMsg(`Continuação: ${ok} criadas, ${skipped} já existiam.`);
      qc.invalidateQueries({ queryKey: ["admin-locations"] });
      qc.invalidateQueries({ queryKey: ["local-seo-progress"] });
    },
  });

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Crie subpáginas de localidade (cidade/bairro) voltadas para <strong>empreendedores e gestores</strong> da região. Cada localidade gera uma página otimizada com a combinação <strong>tema + localidade</strong> e fica disponível em <code className="rounded bg-secondary/40 px-1">/servicos/[slug]</code>.
      </p>

      <LocalSeoPromptEditor />

      <form onSubmit={(e) => { e.preventDefault(); setMsg(null); if (city.trim()) gen.mutate(); }}
        className="card-glass grid gap-3 md:grid-cols-4">
        <div className="md:col-span-4">
          <label className="flex items-center gap-2 text-xs font-medium text-foreground">
            <input type="checkbox" checked={useCustomKeyword} onChange={(e) => setUseCustomKeyword(e.target.checked)} />
            Usar palavra-chave personalizada (em vez de um serviço cadastrado)
          </label>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Marque para gerar páginas a partir de qualquer palavra-chave — ex.: <em>"como abrir uma empresa MEI"</em> + cidade. O slug é criado automaticamente a partir da palavra-chave + cidade.
          </p>
        </div>
        <div className="md:col-span-2">
          {useCustomKeyword ? (
            <>
              <label className="text-xs font-medium text-muted-foreground">Palavra-chave</label>
              <input value={customKeyword} onChange={(e) => setCustomKeyword(e.target.value)}
                placeholder="Ex: contabilidade para MEI"
                className="mt-1 w-full rounded-lg border border-primary/40 bg-background/60 px-3 py-2 text-sm" />
            </>
          ) : (
            <>
              <label className="text-xs font-medium text-muted-foreground">Serviço</label>
              <select value={serviceSlug} onChange={(e) => setServiceSlug(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm">
                {available.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
            </>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Cidade</label>
          <input required value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ex: Rio de Janeiro"
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Bairro (opcional)</label>
          <input value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} placeholder="Ex: Centro"
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <label className="md:col-span-3 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} />
          Publicar imediatamente
        </label>
        <div className="md:col-span-4">
          <label className="text-xs font-medium text-muted-foreground">
            Instrução sobre o tema do texto (opcional)
          </label>
          <textarea
            rows={3}
            value={topicInstruction}
            onChange={(e) => setTopicInstruction(e.target.value)}
            placeholder="Ex: foque em famílias que estão se mudando para o bairro; destaque entrega rápida em Copacabana e parcelamento; reforce conforto para quem trabalha de casa."
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Esta instrução é aplicada tanto na geração individual quanto na geração em massa abaixo.
          </p>
        </div>
        <div className="md:col-span-4 flex items-center justify-between">
          {gen.error && <p className="text-sm text-destructive">{(gen.error as Error).message}</p>}
          {msg && <p className="text-sm text-primary">{msg}</p>}
          <button type="submit" disabled={gen.isPending} className="btn-primary ml-auto">
            {gen.isPending ? "Gerando..." : "Gerar página"}
          </button>
        </div>
      </form>

      <div className="card-glass">
        <h3 className="text-sm font-semibold">Geração em massa de localidades</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Use o serviço selecionado acima e cole <strong>uma localidade por linha</strong>. Aceita os formatos: <code>Cidade</code> ou <code>Cidade | Bairro</code> ou <code>Cidade - Bairro</code>.
        </p>
        <textarea rows={8} value={bulk} onChange={(e) => setBulk(e.target.value)}
          placeholder={"Rio de Janeiro\nRio de Janeiro | Centro\nErechim - Bela Vista\nPasso Fundo"}
          className="mt-2 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex gap-3 text-sm">
            {bulkMsg && <span className="text-primary">{bulkMsg}</span>}
            {bulkGen.error && <span className="text-destructive">{(bulkGen.error as Error).message}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={continueBatch.isPending || isRunning}
              onClick={() => { setBulkMsg(null); continueBatch.mutate(); }}
              title="Re-executa o último lote enviado, pulando as localidades já criadas — útil quando a geração parou no meio."
              className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-medium hover:bg-secondary/40 disabled:opacity-50">
              {continueBatch.isPending ? "Continuando..." : "Continuar último lote"}
            </button>
            <button type="button" disabled={bulkGen.isPending || !bulk.trim()}
              onClick={() => { setBulkMsg(null); bulkGen.mutate(); }}
              className="btn-primary disabled:opacity-50">
              {bulkGen.isPending ? "Gerando em lote..." : "Gerar todas as localidades"}
            </button>
          </div>
        </div>
        {continueBatch.error && (
          <p className="mt-2 text-xs text-destructive">{(continueBatch.error as Error).message}</p>
        )}

        {(isRunning || (p && p.local_seo_batch_finished_at && total > 0)) && (
          <div className="mt-4 rounded-xl border border-border bg-background/40 p-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {isRunning ? (
                  <>⏳ Processando lote — {done}/{total} concluídas</>
                ) : (
                  <>✅ Último lote: {done}/{total} concluídas</>
                )}
              </span>
              <span>{pct}%</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-secondary/60">
              <div
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-2">
                <div className="text-muted-foreground">Criadas</div>
                <div className="text-base font-semibold text-primary">{processedCount}</div>
              </div>
              <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-2">
                <div className="text-muted-foreground">Puladas</div>
                <div className="text-base font-semibold">{skippedCount}</div>
                <div className="text-[10px] text-muted-foreground">já existiam</div>
              </div>
              <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-2">
                <div className="text-muted-foreground">Falharam</div>
                <div className={`text-base font-semibold ${failed > 0 ? "text-destructive" : ""}`}>{failed}</div>
              </div>
            </div>
            {isRunning && p?.local_seo_batch_current && (
              <p className="mt-2 text-xs text-muted-foreground">
                Gerando agora: <span className="font-mono">{p.local_seo_batch_current}</span>
                <span className="ml-2 italic">— pode levar alguns segundos por localidade, o sistema não travou.</span>
              </p>
            )}
            {p?.local_seo_batch_last_error && (
              <p className="mt-2 text-xs text-destructive">Último erro: {p.local_seo_batch_last_error}</p>
            )}
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-4 rounded-xl border border-border bg-background/40 p-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Relatório de execuções</h4>
              <span className="text-[11px] text-muted-foreground">últimas {history.length} rodadas</span>
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr className="border-b border-border/60">
                    <th className="py-1.5 text-left font-medium">Quando</th>
                    <th className="py-1.5 text-left font-medium">Serviço</th>
                    <th className="py-1.5 text-right font-medium">Total</th>
                    <th className="py-1.5 text-right font-medium">Criadas</th>
                    <th className="py-1.5 text-right font-medium">Puladas</th>
                    <th className="py-1.5 text-right font-medium">Falharam</th>
                    <th className="py-1.5 text-right font-medium">Duração</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => {
                    const started = new Date(h.started_at);
                    const when = started.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
                    const secs = Math.max(1, Math.round((h.duration_ms || 0) / 1000));
                    const dur = secs >= 60 ? `${Math.floor(secs / 60)}m ${secs % 60}s` : `${secs}s`;
                    return (
                      <tr key={`${h.started_at}-${i}`} className="border-b border-border/30 last:border-0" title={h.last_error || ""}>
                        <td className="py-1.5">{when}</td>
                        <td className="py-1.5">{h.service_label}</td>
                        <td className="py-1.5 text-right tabular-nums">{h.total}</td>
                        <td className="py-1.5 text-right tabular-nums text-primary">{h.processed}</td>
                        <td className="py-1.5 text-right tabular-nums">{h.skipped}</td>
                        <td className={`py-1.5 text-right tabular-nums ${h.failed > 0 ? "text-destructive" : ""}`}>{h.failed}</td>
                        <td className="py-1.5 text-right tabular-nums text-muted-foreground">{dur}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="card-glass flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Páginas com pouco conteúdo</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Detecta páginas locais cujo corpo ficou abaixo de ~800 caracteres (geração truncada) e regera o conteúdo de todas em sequência.
          </p>
          {regenThin.data && (
            <p className="mt-1 text-xs text-primary">
              {(regenThin.data as any).regenerated}/{(regenThin.data as any).thin} regeneradas (de {(regenThin.data as any).scanned} verificadas).
            </p>
          )}
          {regenThin.error && (
            <p className="mt-1 text-xs text-destructive">{(regenThin.error as Error).message}</p>
          )}
        </div>
        <button type="button" disabled={regenThin.isPending}
          onClick={() => regenThin.mutate()}
          className="btn-primary disabled:opacity-50">
          {regenThin.isPending ? "Regenerando..." : "Regenerar páginas em branco"}
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Página</th>
              <th className="px-4 py-3">Serviço</th>
              <th className="px-4 py-3">Localidade</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Publicado</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Carregando...</td></tr>}
            {list.data?.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Nenhuma página local criada ainda.</td></tr>}
            {list.data?.map((l: any) => {
              const isThin = (l.body_length ?? 0) < 800;
              return (
              <tr key={l.id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">
                  <a href={`/servicos/${l.slug}`} target="_blank" rel="noopener" className="hover:underline">{l.title}</a>
                  <div className="text-xs font-mono text-muted-foreground">/servicos/{l.slug}</div>
                  {isThin && (
                    <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive">
                      ⚠ Página com pouco conteúdo ({l.body_length} chars)
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">{l.service_label}</td>
                <td className="px-4 py-3">{l.neighborhood ? `${l.neighborhood}, ${l.city}` : l.city}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${l.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {l.published ? "Publicado" : "Rascunho"}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{l.published_at ? formatDateBR(l.published_at) : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => regenOne.mutate(l.id)}
                    disabled={regenOne.isPending}
                    className="mr-3 text-primary hover:underline disabled:opacity-50"
                  >
                    {regenOne.isPending && regenOne.variables === l.id ? "Regenerando..." : "Regenerar"}
                  </button>
                  <button onClick={() => { if (confirm(`Excluir "${l.title}"?`)) del.mutate(l.id); }} className="text-destructive hover:underline">Excluir</button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}
// ───────── Modelos (Top 6) Tab ─────────
type ProductModelDraft = {
  id?: string;
  slug: string;
  name: string;
  brand: string;
  short_desc: string;
  long_intro: string; // newline-separated paragraphs
  pros: string;
  cons: string;
  best_for: string;
  not_ideal_for: string;
  specs: string; // "Label: value" per line
  faq: string; // "Q: ...\nA: ..." blocks separated by blank line
  highlights: string;
  image_url: string;
  image_alt: string;
  price_from_brl: string;
  rating: string;
  reviews_count: string;
  affiliate_amazon: string;
  affiliate_mercadolivre: string;
  affiliate_magalu: string;
  affiliate_primary_url: string;
  affiliate_marketplace: string;
  affiliate_cta_label: string;
  ranking_position: string;
  active: boolean;
  meta_title: string;
  meta_description: string;
  spec_brief: string;
};

const EMPTY_MODEL: ProductModelDraft = {
  slug: "", name: "", brand: "", short_desc: "", long_intro: "", pros: "", cons: "",
  best_for: "", not_ideal_for: "", specs: "", faq: "", highlights: "",
  image_url: "", image_alt: "", price_from_brl: "", rating: "", reviews_count: "",
  affiliate_amazon: "", affiliate_mercadolivre: "", affiliate_magalu: "",
  affiliate_primary_url: "", affiliate_marketplace: "Amazon", affiliate_cta_label: "",
  ranking_position: "", active: true, meta_title: "", meta_description: "", spec_brief: "",
};

function lineList(s: string): string[] {
  return s.split("\n").map((l) => l.trim()).filter(Boolean);
}
function parseSpecs(s: string): { label: string; value: string }[] {
  return lineList(s)
    .map((l) => {
      const idx = l.indexOf(":");
      if (idx < 0) return null;
      const label = l.slice(0, idx).trim();
      const value = l.slice(idx + 1).trim();
      if (!label || !value) return null;
      return { label, value };
    })
    .filter((x): x is { label: string; value: string } => !!x);
}
function parseFaq(s: string): { q: string; a: string }[] {
  return s.split(/\n\s*\n/).map((block) => {
    const qMatch = block.match(/^Q:\s*(.+)$/im);
    const aMatch = block.match(/^A:\s*([\s\S]+)$/im);
    if (!qMatch || !aMatch) return null;
    return { q: qMatch[1].trim(), a: aMatch[1].trim() };
  }).filter((x): x is { q: string; a: string } => !!x);
}

function rowToDraft(row: any): ProductModelDraft {
  return {
    id: row.id,
    slug: row.slug ?? "",
    name: row.name ?? "",
    brand: row.brand ?? "",
    short_desc: row.short_desc ?? "",
    long_intro: Array.isArray(row.long_intro) ? row.long_intro.join("\n\n") : "",
    pros: Array.isArray(row.pros) ? row.pros.join("\n") : "",
    cons: Array.isArray(row.cons) ? row.cons.join("\n") : "",
    best_for: Array.isArray(row.best_for) ? row.best_for.join("\n") : "",
    not_ideal_for: Array.isArray(row.not_ideal_for) ? row.not_ideal_for.join("\n") : "",
    specs: Array.isArray(row.specs) ? row.specs.map((s: any) => `${s.label}: ${s.value}`).join("\n") : "",
    faq: Array.isArray(row.faq) ? row.faq.map((f: any) => `Q: ${f.q}\nA: ${f.a}`).join("\n\n") : "",
    highlights: Array.isArray(row.highlights) ? row.highlights.join("\n") : "",
    image_url: row.image_url ?? "",
    image_alt: row.image_alt ?? "",
    price_from_brl: row.price_from_brl != null ? String(row.price_from_brl) : "",
    rating: row.rating != null ? String(row.rating) : "",
    reviews_count: row.reviews_count != null ? String(row.reviews_count) : "",
    affiliate_amazon: row.affiliate_amazon ?? "",
    affiliate_mercadolivre: row.affiliate_mercadolivre ?? "",
    affiliate_magalu: row.affiliate_magalu ?? "",
    affiliate_primary_url: row.affiliate_primary_url ?? "",
    affiliate_marketplace: row.affiliate_marketplace ?? "Amazon",
    affiliate_cta_label: row.affiliate_cta_label ?? "",
    ranking_position: row.ranking_position != null ? String(row.ranking_position) : "",
    active: row.active !== false,
    meta_title: row.meta_title ?? "",
    meta_description: row.meta_description ?? "",
    spec_brief: row.spec_brief ?? "",
  };
}

function draftToPayload(d: ProductModelDraft) {
  return {
    id: d.id,
    slug: d.slug.trim(),
    name: d.name.trim(),
    brand: d.brand.trim() || null,
    short_desc: d.short_desc.trim() || null,
    long_intro: d.long_intro.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
    pros: lineList(d.pros),
    cons: lineList(d.cons),
    best_for: lineList(d.best_for),
    not_ideal_for: lineList(d.not_ideal_for),
    specs: parseSpecs(d.specs),
    faq: parseFaq(d.faq),
    highlights: lineList(d.highlights),
    image_url: d.image_url.trim() || null,
    image_alt: d.image_alt.trim() || null,
    price_from_brl: d.price_from_brl.trim() ? Number(d.price_from_brl.replace(",", ".")) : null,
    rating: d.rating.trim() ? Number(d.rating.replace(",", ".")) : null,
    reviews_count: d.reviews_count.trim() ? Number(d.reviews_count) : null,
    affiliate_amazon: d.affiliate_amazon.trim() || null,
    affiliate_mercadolivre: d.affiliate_mercadolivre.trim() || null,
    affiliate_magalu: d.affiliate_magalu.trim() || null,
    affiliate_primary_url: d.affiliate_primary_url.trim() || null,
    affiliate_marketplace: d.affiliate_marketplace.trim() || null,
    affiliate_cta_label: d.affiliate_cta_label.trim() || null,
    ranking_position: d.ranking_position.trim() ? Number(d.ranking_position) : null,
    active: d.active,
    meta_title: d.meta_title.trim() || null,
    meta_description: d.meta_description.trim() || null,
    spec_brief: d.spec_brief.trim() || null,
  };
}

function ModelsTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-product-models"], queryFn: () => listProductModels() });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProductModelDraft>(EMPTY_MODEL);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [uploadingImg, setUploadingImg] = useState(false);

  async function uploadModelImage(file: File) {
    try {
      setUploadingImg(true);
      setFeedback(null);
      const base64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
      });
      const r = await uploadPostImage({ data: { fileName: file.name, contentType: file.type || "image/jpeg", base64 } });
      setDraft((d) => ({ ...d, image_url: r.url }));
      setFeedback("Imagem enviada.");
    } catch (e: any) {
      setFeedback(e?.message ?? "Falha no upload da imagem.");
    } finally {
      setUploadingImg(false);
    }
  }

  const loadOne = useMutation({
    mutationFn: (id: string) => getProductModelForEdit({ data: { id } }),
    onSuccess: (row) => { if (row) { setDraft(rowToDraft(row)); setEditingId(row.id); setFeedback(null); } },
  });
  const save = useMutation({
    mutationFn: () => saveProductModel({ data: draftToPayload(draft) as any }),
    onSuccess: (r: any) => {
      setEditingId(r.id);
      setDraft((d) => ({ ...d, id: r.id }));
      setFeedback("Modelo salvo.");
      qc.invalidateQueries({ queryKey: ["admin-product-models"] });
      qc.invalidateQueries({ queryKey: ["top-models"] });
      qc.invalidateQueries({ queryKey: ["site-models"] });
      qc.invalidateQueries({ queryKey: ["search-models"] });
      qc.invalidateQueries({ queryKey: ["pillar", "melhores-robos-aspiradores"] });
    },
    onError: (e: any) => setFeedback(e?.message ?? "Erro ao salvar."),
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteProductModel({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-product-models"] });
      qc.invalidateQueries({ queryKey: ["top-models"] });
      qc.invalidateQueries({ queryKey: ["site-models"] });
      qc.invalidateQueries({ queryKey: ["search-models"] });
      qc.invalidateQueries({ queryKey: ["pillar", "melhores-robos-aspiradores"] });
      if (editingId) { setEditingId(null); setDraft(EMPTY_MODEL); }
    },
  });
  const gen = useMutation({
    mutationFn: () => generateProductModelFromBrief({ data: {
      name: draft.name, brand: draft.brand || undefined, brief: draft.spec_brief,
    }}),
    onSuccess: (out: any) => {
      setDraft((d) => ({
        ...d,
        short_desc: out.short_desc ?? d.short_desc,
        long_intro: Array.isArray(out.long_intro) ? out.long_intro.join("\n\n") : d.long_intro,
        pros: Array.isArray(out.pros) ? out.pros.join("\n") : d.pros,
        cons: Array.isArray(out.cons) ? out.cons.join("\n") : d.cons,
        best_for: Array.isArray(out.best_for) ? out.best_for.join("\n") : d.best_for,
        not_ideal_for: Array.isArray(out.not_ideal_for) ? out.not_ideal_for.join("\n") : d.not_ideal_for,
        specs: Array.isArray(out.specs) ? out.specs.map((s: any) => `${s.label}: ${s.value}`).join("\n") : d.specs,
        faq: Array.isArray(out.faq) ? out.faq.map((f: any) => `Q: ${f.q}\nA: ${f.a}`).join("\n\n") : d.faq,
        highlights: Array.isArray(out.highlights) ? out.highlights.join("\n") : d.highlights,
        meta_title: out.meta_title ?? d.meta_title,
        meta_description: out.meta_description ?? d.meta_description,
      }));
      setFeedback("Conteúdo gerado pela IA. Revise e salve.");
    },
    onError: (e: any) => setFeedback(e?.message ?? "Erro ao gerar com IA."),
  });

  const setField = <K extends keyof ProductModelDraft>(k: K, v: ProductModelDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
      <aside className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Modelos</h3>
          <button
            onClick={() => { setEditingId(null); setDraft(EMPTY_MODEL); setFeedback(null); }}
            className="text-xs font-medium text-primary"
          >+ Novo</button>
        </div>
        <ul className="space-y-1">
          {list.data?.map((m: any) => (
            <li key={m.id}>
              <button
                onClick={() => loadOne.mutate(m.id)}
                className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                  editingId === m.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{m.name}</span>
                  {m.ranking_position != null && (
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">#{m.ranking_position}</span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs text-muted-foreground">/modelos/{m.slug}</div>
              </button>
            </li>
          ))}
          {(!list.data || list.data.length === 0) && (
            <li className="text-sm text-muted-foreground">Nenhum modelo cadastrado.</li>
          )}
        </ul>
      </aside>

      <section className="space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm">
              <span className="text-muted-foreground">Nome</span>
              <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                value={draft.name} onChange={(e) => setField("name", e.target.value)} />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground">Slug (url)</span>
              <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                value={draft.slug} onChange={(e) => setField("slug", e.target.value)} placeholder="ex: guia-mei-2026" />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground">Marca</span>
              <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                value={draft.brand} onChange={(e) => setField("brand", e.target.value)} />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground">Posição no Top (1–6, vazio = oculto)</span>
              <input type="number" className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                value={draft.ranking_position} onChange={(e) => setField("ranking_position", e.target.value)} />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="text-muted-foreground">Descrição curta (até 400 chars)</span>
              <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                value={draft.short_desc} onChange={(e) => setField("short_desc", e.target.value)} />
            </label>
          </div>
        </div>

        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
          <h4 className="text-sm font-semibold">Gerar página com IA</h4>
          <p className="mt-1 text-xs text-muted-foreground">
            Cole abaixo as especificações cruas (sucção, autonomia, mapeamento, etc.). A IA preenche intro, prós/contras, ficha, FAQ e meta.
          </p>
          <textarea rows={6}
            className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            placeholder="Sucção 4000 Pa, mapeamento LiDAR, bateria 5200 mAh, autonomia 180 min, esvaziamento automático..."
            value={draft.spec_brief}
            onChange={(e) => setField("spec_brief", e.target.value)} />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              onClick={() => gen.mutate()}
              disabled={gen.isPending || !draft.name.trim() || draft.spec_brief.trim().length < 20}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >{gen.isPending ? "Gerando..." : "Gerar conteúdo com IA"}</button>
            <span className="text-xs text-muted-foreground">Requer nome + briefing ({"≥"}20 caracteres).</span>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 rounded-2xl border border-border bg-card p-5">
          <label className="text-sm md:col-span-2">
            <span className="text-muted-foreground">Parágrafos do review (separe com linha em branco)</span>
            <textarea rows={6} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={draft.long_intro} onChange={(e) => setField("long_intro", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Prós (1 por linha)</span>
            <textarea rows={5} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={draft.pros} onChange={(e) => setField("pros", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Contras (1 por linha)</span>
            <textarea rows={5} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={draft.cons} onChange={(e) => setField("cons", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Ideal para (1 por linha)</span>
            <textarea rows={4} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={draft.best_for} onChange={(e) => setField("best_for", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Não recomendado para (1 por linha)</span>
            <textarea rows={4} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={draft.not_ideal_for} onChange={(e) => setField("not_ideal_for", e.target.value)} />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-muted-foreground">Ficha técnica (uma por linha — "Rótulo: valor")</span>
            <textarea rows={6} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              placeholder={"Sucção: 4000 Pa\nAutonomia: 180 min\nMapeamento: LiDAR"}
              value={draft.specs} onChange={(e) => setField("specs", e.target.value)} />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-muted-foreground">FAQ (blocos "Q: ... / A: ...", separados por linha em branco)</span>
            <textarea rows={6} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              placeholder={"Q: Funciona em carpete?\nA: Sim, com modo turbo..."}
              value={draft.faq} onChange={(e) => setField("faq", e.target.value)} />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-muted-foreground">Principais características (1 por linha)</span>
            <textarea rows={4} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={draft.highlights} onChange={(e) => setField("highlights", e.target.value)} />
          </label>
        </div>

        <div className="grid gap-3 md:grid-cols-2 rounded-2xl border border-border bg-card p-5">
          <label className="text-sm md:col-span-2">
            <span className="text-muted-foreground">Imagem (URL ou upload)</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.image_url} onChange={(e) => setField("image_url", e.target.value)}
              placeholder="https://... ou faça upload abaixo" />
            <div className="mt-2 flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border bg-background px-3 py-2 text-xs hover:bg-secondary">
                {uploadingImg ? "Enviando..." : "📤 Upload da imagem"}
                <input type="file" accept="image/*" className="hidden" disabled={uploadingImg}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadModelImage(f); e.currentTarget.value = ""; }} />
              </label>
              {draft.image_url && (
                <img src={draft.image_url} alt={draft.image_alt || draft.name || "Produto"}
                  className="h-12 w-12 rounded-md object-cover border border-border" />
              )}
            </div>
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-muted-foreground">ALT da imagem</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.image_alt} onChange={(e) => setField("image_alt", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Preço a partir de (R$)</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.price_from_brl} onChange={(e) => setField("price_from_brl", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Avaliação (0–5)</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.rating} onChange={(e) => setField("rating", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Qtde de reviews</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.reviews_count} onChange={(e) => setField("reviews_count", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Marketplace principal</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.affiliate_marketplace} onChange={(e) => setField("affiliate_marketplace", e.target.value)} />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-muted-foreground">Link primário (afiliado)</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.affiliate_primary_url} onChange={(e) => setField("affiliate_primary_url", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Link Amazon</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.affiliate_amazon} onChange={(e) => setField("affiliate_amazon", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Link Mercado Livre</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.affiliate_mercadolivre} onChange={(e) => setField("affiliate_mercadolivre", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Link Magalu</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.affiliate_magalu} onChange={(e) => setField("affiliate_magalu", e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground">Texto do botão (CTA)</span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.affiliate_cta_label} onChange={(e) => setField("affiliate_cta_label", e.target.value)} placeholder="Ver na Amazon" />
          </label>
        </div>

        <div className="grid gap-3 md:grid-cols-2 rounded-2xl border border-border bg-card p-5">
          <label className="text-sm md:col-span-2">
            <span className="flex items-center justify-between text-muted-foreground">
              <span>Meta title (SEO)</span>
              <span className={`text-xs ${draft.meta_title.length < 50 || draft.meta_title.length > 60 ? "text-amber-600" : "text-emerald-600"}`}>
                {draft.meta_title.length}/50–60
              </span>
            </span>
            <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
              value={draft.meta_title} onChange={(e) => setField("meta_title", e.target.value)} />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="flex items-center justify-between text-muted-foreground">
              <span>Meta description (SEO)</span>
              <span className={`text-xs ${draft.meta_description.length < 140 || draft.meta_description.length > 160 ? "text-amber-600" : "text-emerald-600"}`}>
                {draft.meta_description.length}/140–160
              </span>
            </span>
            <textarea rows={2} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={draft.meta_description} onChange={(e) => setField("meta_description", e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.active} onChange={(e) => setField("active", e.target.checked)} />
            Ativo (exibido publicamente)
          </label>
        </div>

        {feedback && <p className="text-sm text-muted-foreground">{feedback}</p>}

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !draft.name.trim() || !draft.slug.trim()}
            className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >{save.isPending ? "Salvando..." : "Salvar modelo"}</button>
          {editingId && (
            <a href={`/modelos/${draft.slug}`} target="_blank" rel="noreferrer"
              className="rounded-lg border border-border px-4 py-2 text-sm">Abrir página</a>
          )}
          {editingId && (
            <button
              onClick={() => { if (confirm("Excluir este modelo?")) del.mutate(editingId); }}
              className="ml-auto rounded-lg border border-rose-500/40 px-4 py-2 text-sm text-rose-400"
            >Excluir</button>
          )}
        </div>
      </section>
    </div>
  );
}
